<template>
  <div>
    <b-card no-body>

      <b-card-body class="bg-light pb-5">
        <!-- 模式切换与强度排序工具条 (置于搜索框上方，上下紧凑无多余间距) -->
        <div class="row mb-2">
          <div :class="[showMonitorPanel ? 'col-12 col-lg-9 pr-lg-4' : 'col-12 col-lg-10']">
            <div class="d-flex flex-wrap align-items-center justify-content-between p-2 rounded-lg bg-card shadow-sm border">
              <div class="d-flex flex-wrap align-items-center">
                <!-- 排序大类切换 -->
                <b-button-group size="sm" class="mr-2 mb-1 mb-sm-0 shadow-sm">
                  <b-button 
                    :variant="sortMode === 'default' ? 'primary' : 'outline-secondary'" 
                    @click="setSortMode('default')"
                    class="px-3 font-weight-bold"
                  >
                    <b-icon icon="sort-down" class="mr-1"></b-icon> 默认
                  </b-button>
                  <b-button 
                    :variant="sortMode === 'strength' ? 'primary' : 'outline-secondary'" 
                    @click="setSortMode('strength')"
                    class="px-3 font-weight-bold d-inline-flex align-items-center"
                  >
                    <i class="fas fa-crown mr-1 text-warning"></i> 强度排名
                    <b-badge v-if="!userHasPermission" variant="warning" class="ml-1 px-1">VIP</b-badge>
                    <span 
                      id="topic-rank-help-trigger" 
                      class="ml-1 cursor-pointer" 
                      :class="sortMode === 'strength' ? 'text-white-50' : 'text-muted'"
                      @click.stop
                    >
                      <b-icon icon="question-circle-fill" font-scale="0.9"></b-icon>
                    </span>
                  </b-button>
                </b-button-group>

                <!-- 排名说明 Popover (排名的区别与实战对比) -->
                <b-popover 
                  target="topic-rank-help-trigger" 
                  triggers="hover focus click" 
                  placement="bottom" 
                  boundary="window"
                  custom-class="topic-rank-help-popover"
                >
                  <template #title>
                    <div class="d-flex align-items-center font-weight-bold text-dark">
                      <b-icon icon="info-circle-fill" class="text-primary mr-1"></b-icon> 排名的区别与实战对比
                    </div>
                  </template>
                  <div class="table-responsive mb-0" style="min-width: 320px; max-width: 480px;">
                    <table class="table table-sm table-bordered mb-0" style="font-size: 12px;">
                      <thead class="thead-light">
                        <tr>
                          <th style="width: 100px; white-space: nowrap;">排名维度</th>
                          <th>适合交易风格 / 实战场景</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td class="font-weight-bold text-primary" style="white-space: nowrap;">
                            综合主线<br>
                            <small class="text-muted font-weight-normal">(默认推荐)</small>
                          </td>
                          <td class="text-muted">
                            <strong class="text-dark">中大资金、趋势波段、主线主升浪</strong><br>
                            筛选既有空间高度、又有板块资金容量的核心主流题材。
                          </td>
                        </tr>
                        <tr>
                          <td class="font-weight-bold text-danger" style="white-space: nowrap;">超短主线</td>
                          <td class="text-muted">
                            <strong class="text-dark">打板接力、龙头战法、超短游资</strong><br>
                            捕捉短线资金合力最强、梯队最清晰、情绪辨识度最高的风口。
                          </td>
                        </tr>
                        <tr>
                          <td class="font-weight-bold text-success" style="white-space: nowrap;">稳健共振</td>
                          <td class="text-muted">
                            <strong class="text-dark">机构容量、低吸潜伏、ETF配置</strong><br>
                            适合寻找全面普涨、多股涨停共振的大行业主线。
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </b-popover>

                <!-- 强度子选项 -->
                <b-button-group v-if="sortMode === 'strength'" size="sm" class="mr-2 mb-1 mb-sm-0 shadow-sm">
                  <b-button 
                    :variant="strengthSort === 'strength' ? 'info' : 'outline-secondary'" 
                    @click="changeStrengthSort('strength')"
                    title="综合模型：融合超短爆发、波段龙头与板块共振"
                  >
                    综合主线
                  </b-button>
                  <b-button 
                    :variant="strengthSort === 'score_b' ? 'info' : 'outline-secondary'" 
                    @click="changeStrengthSort('score_b')"
                    title="超短主线：连板梯队/20日波段主升/防核按钮"
                  >
                    超短主线
                  </b-button>
                  <b-button 
                    :variant="strengthSort === 'score_a' ? 'info' : 'outline-secondary'" 
                    @click="changeStrengthSort('score_a')"
                    title="稳健共振：涨停数量/领涨广度/板块动量"
                  >
                    稳健共振
                  </b-button>
                </b-button-group>
              </div>

              <!-- 日历与快捷切换（仅在强度排名模式展示） -->
              <div v-if="sortMode === 'strength'" class="d-flex align-items-center mt-2 mt-md-0 flex-wrap">
                <b-button-group size="sm" class="mr-2 shadow-sm">
                  <b-button variant="outline-secondary" @click="shiftTradeDate(-1)" title="前一交易日">
                    <b-icon icon="chevron-left"></b-icon> 前一日
                  </b-button>
                  <b-button variant="outline-secondary" @click="resetToLatestDate" title="回到最新">
                    最新
                  </b-button>
                  <b-button variant="outline-secondary" @click="shiftTradeDate(1)" :disabled="isLatestDate" title="后一交易日">
                    后一日 <b-icon icon="chevron-right"></b-icon>
                  </b-button>
                </b-button-group>

                <div class="date-picker-wrapper" style="width: 145px;">
                  <b-form-datepicker 
                    id="topic-strength-datepicker" 
                    v-model="selectedRankDate" 
                    size="sm" 
                    :max="todayStr"
                    locale="zh"
                    placeholder="选择日期"
                    :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                    @input="onDateChanged"
                    class="shadow-sm border-0"
                  ></b-form-datepicker>
                </div>

                <b-badge v-if="currentTradeDate" variant="soft-primary" class="ml-2 py-1 px-2 font-weight-bold">
                  {{ currentTradeDate }}
                </b-badge>
              </div>
            </div>
          </div>
        </div>

        <!-- 搜索与操作条 (紧随排序按钮下方) -->
        <div class="row mb-3">
          <div :class="[showMonitorPanel ? 'col-12 col-lg-9 pr-lg-4' : 'col-12 col-lg-10', 'd-flex align-items-center']">
            <b-input-group class="search-group shadow-sm flex-grow-1">
              <b-input-group-prepend is-text>
                <b-icon icon="search" class="text-muted"></b-icon>
              </b-input-group-prepend>
              <b-form-input 
                v-model="searchQuery" 
                placeholder="搜索题材库名称或内容..." 
                @input="onSearchInput"
              ></b-form-input>
            </b-input-group>
            
            <b-button v-if="canEdit" variant="primary" class="ml-2 ml-md-3 rounded-circle d-flex align-items-center justify-content-center shadow-sm" @click="openCreateSidebar" style="width: 48px; height: 48px; flex-shrink: 0; padding: 0;">
              <b-icon icon="plus" font-scale="1.8"></b-icon>
            </b-button>

            <!-- 管理员监控工具 -->
            <b-dropdown v-if="userRoles.includes('admin')" right no-caret variant="link" class="ml-2" toggle-class="p-0">
              <template #button-content>
                <div class="admin-tool-btn rounded-circle d-flex align-items-center justify-content-center shadow-sm bg-warning text-white" style="width: 48px; height: 48px;">
                  <b-icon icon="gear-fill" font-scale="1.4"></b-icon>
                </div>
              </template>
              <b-dropdown-header>监控管理</b-dropdown-header>
              <b-dropdown-item @click="triggerMonitor">
                <b-icon icon="lightning" class="mr-2"></b-icon> 手动触发监控
              </b-dropdown-item>
              <b-dropdown-item @click="refreshMonitor">
                <b-icon icon="arrow-clockwise" class="mr-2"></b-icon> 刷新监控名单
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>

        <div class="row justify-content-center">
          <!-- 题材列表区域 -->
          <div :class="[showMonitorPanel ? 'col-12 col-lg-9 pr-lg-4' : 'col-12 col-lg-10']">
            <div v-if="loading && !tables.length" class="text-center my-5 py-5">
              <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
              <div class="mt-3 text-muted font-weight-bold">正在加载题材库...</div>
            </div>

            <div v-else-if="!tables.length" class="text-center my-5 py-5 bg-card rounded-lg shadow-sm">
              <b-icon icon="info-circle" font-scale="3" class="text-muted opacity-5"></b-icon>
              <div class="mt-3 text-muted">暂无题材数据</div>
            </div>

            <div v-else class="topic-list">
              <!-- 强度排名列表 -->
              <div v-if="sortMode === 'strength'" class="strength-topic-list">
                <div 
                  v-for="(table, tIdx) in tables" 
                  :key="table.id" 
                  :class="['topic-card mb-3 p-3 d-flex align-items-center cursor-pointer transition-all', table.is_deleted ? 'topic-card-deleted' : '']"
                  @click="onRowClicked(table)"
                >
                  <!-- 排名序号 -->
                  <div :class="['topic-rank mr-3', getRankClass(tIdx)]">
                    {{ (currentPage - 1) * perPage + tIdx + 1 }}
                  </div>

                  <div class="flex-grow-1 min-width-0">
                    <div class="d-flex align-items-start justify-content-between mb-1">
                      <div class="d-flex align-items-center flex-wrap overflow-hidden mr-2">
                        <b-badge v-if="table.is_deleted" variant="danger" class="mr-2 px-2 py-1 small shadow-sm">已删除</b-badge>
                        <h5 class="topic-title mb-0 text-truncate font-weight-bold text-primary mr-2 cursor-pointer" @click.stop="showTopicKline(table)">
                          {{ formatTopicName(table.name) }}
                        </h5>
                        <b-icon v-if="canEdit" icon="pencil-square" class="text-muted hover-primary flex-shrink-0 mr-2" @click.stop="editTable(table)"></b-icon>

                        <!-- 得分徽标 -->
                        <span 
                          class="mr-1 flex-shrink-0 text-white font-weight-bold"
                          style="font-size: 11px; padding: 2px 7px; border-radius: 4px; background: linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%); box-shadow: 0 2px 4px rgba(255, 65, 108, 0.3);"
                        >
                          🔥 {{ table.score !== undefined ? table.score : '--' }}分
                        </span>

                        <!-- 稳健与超短得分明细 -->
                        <span 
                          class="mr-1 flex-shrink-0 text-muted small px-1 py-0 rounded"
                          style="font-size: 11px; background-color: rgba(128, 128, 128, 0.1); border: 1px solid rgba(128, 128, 128, 0.15);"
                          title="方案A稳健得分 / 方案B超短得分"
                        >
                          稳:{{ table.score_a || 0 }} | 超:{{ table.score_b || 0 }}
                        </span>

                        <!-- 阶段生命周期 Tag -->
                        <b-badge 
                          v-if="table.stage_tag" 
                          :variant="getStageTagVariant(table.stage_tag)" 
                          class="mr-1 flex-shrink-0 px-2 py-1 shadow-sm font-weight-bold" 
                          style="font-size: 11px;"
                        >
                          {{ table.stage_tag }}
                        </b-badge>

                        <!-- 量化策略精选 Tag -->
                        <b-badge 
                          v-if="table.is_v2v3_candidate" 
                          variant="warning" 
                          class="mr-1 flex-shrink-0 px-2 py-1 text-dark shadow-sm font-weight-bold" 
                          style="font-size: 11px;"
                        >
                          <i class="fas fa-crown mr-1"></i>精选
                        </b-badge>

                        <!-- 60日收盘新高距今天数：H1=今日，H2=上一交易日 -->
                        <b-badge 
                          v-if="table.h_marker" 
                          variant="info" 
                          class="mr-1 flex-shrink-0 px-2 py-1 shadow-sm font-weight-bold" 
                          style="font-size: 11px;"
                        >
                          {{ String(table.h_marker).startsWith('H') ? table.h_marker : 'H' + table.h_marker }}突破
                        </b-badge>

                        <!-- 趋势强势股数量 -->
                        <span 
                          v-if="table.trend_leader_count" 
                          class="mr-1 flex-shrink-0 soft-info font-weight-bold" 
                          style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; cursor: help;"
                          :title="isAdmin ? '近20日波段涨幅达到35%或近30日达到50%，同时近5日回撤不超过12%且当前未跌停的成份股数量。数量越多，说明题材内趋势强势股分布越广。' : ''"
                        >
                          {{ table.trend_leader_count }}只主力
                        </span>

                        <!-- 20日最高波段涨幅 -->
                        <span 
                          v-if="table.max_stock_runup_20d" 
                          class="mr-1 flex-shrink-0 text-danger font-weight-bold" 
                          style="font-size: 11px; padding: 2px 6px; border-radius: 4px; background-color: rgba(220, 53, 69, 0.1);" 
                          title="灵魂核心代表"
                        >
                          最高+{{ table.max_stock_runup_20d }}%
                        </span>

                        <!-- 当日涨跌幅 -->
                        <span 
                          v-if="table.today_pct !== undefined && table.today_pct !== null" 
                          :class="[table.today_pct > 0 ? 'soft-danger' : table.today_pct < 0 ? 'soft-success' : 'bg-secondary text-white', 'mr-1 flex-shrink-0']"
                          style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; font-weight: bold;"
                        >
                          {{ table.today_pct > 0 ? '+' : '' }}{{ table.today_pct.toFixed(2) }}%
                        </span>

                        <!-- 涨跌个股数 -->
                        <span 
                          v-if="table.up_count !== undefined && table.up_count !== null && table.down_count !== undefined && table.down_count !== null"
                          class="mr-1 flex-shrink-0 font-weight-bold"
                          style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; background-color: rgba(128, 128, 128, 0.15); border: 1px solid rgba(128, 128, 128, 0.2);"
                        >
                          <span class="text-danger mr-1">{{ table.up_count }}涨</span>
                          <span class="text-success">{{ table.down_count }}跌</span>
                        </span>

                        <!-- 领涨个数 -->
                        <span 
                          v-if="table.leader_count !== undefined && table.leader_count !== null" 
                          :class="[table.leader_count > 0 ? 'soft-warning' : 'bg-secondary text-white', 'mr-1 flex-shrink-0']"
                          style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; font-weight: bold;"
                        >
                          {{ table.leader_count }} 领涨
                        </span>

                        <!-- 涨停个数 -->
                        <span 
                          v-if="table.limit_up_count !== undefined && table.limit_up_count !== null" 
                          :class="[table.limit_up_count > 0 ? 'bg-danger' : 'bg-secondary', 'text-white flex-shrink-0']"
                          style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; font-weight: bold; opacity: 0.9;"
                        >
                          {{ table.limit_up_count }} 涨停
                        </span>

                        <!-- 打分归因详情 Tooltip 图标 (仅管理员可见) -->
                        <span 
                          v-if="isAdmin && table.reasons" 
                          class="text-info ml-1 cursor-pointer flex-shrink-0"
                          :title="table.reasons"
                        >
                          <b-icon icon="info-circle"></b-icon>
                        </span>
                      </div>

                      <span class="topic-time text-muted x-small flex-shrink-0 ml-2">
                        <b-icon icon="calendar3" class="mr-1"></b-icon>{{ table.trade_date || formatTime(table.updated_time) }}
                      </span>
                    </div>

                    <p class="topic-desc mb-0 text-muted small text-truncate-2">
                      {{ table.content || '暂无描述' }}
                    </p>
                  </div>

                  <div class="ml-3 pl-2 border-left d-none d-md-block">
                    <b-button variant="link" class="p-0 text-primary" @click.stop="selectTable(table)">
                      <b-icon icon="chevron-right" font-scale="1.2"></b-icon>
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- 默认排序列表（含置顶拖拽与普通列表） -->
              <div v-else class="default-topic-list">
                <!-- 拖拽置顶列表 -->
                <draggable v-model="pinnedTables" @end="onPinnedSortEnd" :options="{animation:200, handle:'.drag-handle'}">
                  <div 
                    v-for="(table, tIdx) in pinnedTables" 
                    :key="table.id" 
                    :class="['topic-card mb-3 p-3 d-flex align-items-center cursor-pointer transition-all border-left-danger border-left-3', table.is_deleted ? 'topic-card-deleted' : '']"
                    @click="onRowClicked(table)"
                  >
                    <!-- 拖拽手柄 -->
                    <div v-if="userRoles.includes('admin')" class="drag-handle mr-2 text-muted cursor-move" @click.stop title="拖动排序">
                      <b-icon icon="list" font-scale="1.5"></b-icon>
                    </div>
                    <!-- 装饰性序号 -->
                    <div :class="['topic-rank mr-3', getRankClass(tIdx)]">
                      {{ (currentPage - 1) * perPage + tIdx + 1 }}
                    </div>

                    <div class="flex-grow-1 min-width-0">
                      <div class="d-flex align-items-start justify-content-between mb-1">
                        <div class="d-flex align-items-center overflow-hidden mr-2">
                          <b-badge v-if="userRoles.includes('admin')" variant="danger" class="mr-2 px-2 py-1 small shadow-sm">置顶</b-badge>
                          <b-badge v-if="table.is_deleted" variant="danger" class="mr-2 px-2 py-1 small shadow-sm">已删除</b-badge>
                          <h5 class="topic-title mb-0 text-truncate font-weight-bold text-primary mr-2 cursor-pointer" @click.stop="showTopicKline(table)">{{ formatTopicName(table.name) }}</h5>
                          <b-icon v-if="canEdit" icon="pencil-square" class="text-muted hover-primary flex-shrink-0 mr-2" @click.stop="editTable(table)"></b-icon>
                          <b-icon v-if="userRoles.includes('admin')" icon="arrow-up-circle-fill" class="text-danger flex-shrink-0 ml-1 mr-2" font-scale="1.2" title="取消置顶" @click.stop="toggleTop(table)"></b-icon>
                          <b-icon v-if="userRoles.includes('admin') && !table.is_deleted" icon="trash" class="text-muted hover-danger flex-shrink-0 mr-2" font-scale="1.1" title="逻辑删除" @click.stop="confirmDeleteTopic(table)"></b-icon>
                          <b-icon v-if="userRoles.includes('admin') && table.is_deleted" icon="arrow-counterclockwise" class="text-warning hover-success flex-shrink-0 mr-2" font-scale="1.2" title="恢复题材" @click.stop="confirmRestoreTopic(table)"></b-icon>
                          
                          <!-- 当日涨跌幅 -->
                          <span 
                            v-if="table.today_pct !== undefined && table.today_pct !== null" 
                            :class="[table.today_pct > 0 ? 'soft-danger' : table.today_pct < 0 ? 'soft-success' : 'bg-secondary text-white', 'mr-1 flex-shrink-0']"
                            style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; font-weight: bold;"
                          >
                            {{ table.today_pct > 0 ? '+' : '' }}{{ table.today_pct.toFixed(2) }}%
                          </span>

                          <!-- 涨跌个股数 -->
                          <span 
                            v-if="table.up_count !== undefined && table.up_count !== null && table.down_count !== undefined && table.down_count !== null"
                            class="mr-1 flex-shrink-0 font-weight-bold"
                            style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; background-color: rgba(128, 128, 128, 0.15); border: 1px solid rgba(128, 128, 128, 0.2);"
                          >
                            <span class="text-danger mr-1">{{ table.up_count }}涨</span>
                            <span class="text-success">{{ table.down_count }}跌</span>
                          </span>

                          <!-- 领涨个数 -->
                          <span 
                            v-if="table.leader_count !== undefined && table.leader_count !== null" 
                            :class="[table.leader_count > 0 ? 'soft-warning' : 'bg-secondary text-white', 'mr-1 flex-shrink-0']"
                            style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; font-weight: bold;"
                          >
                            {{ table.leader_count }} 领涨
                          </span>

                          <!-- 涨停个数 -->
                          <span 
                            v-if="table.limit_up_count !== undefined && table.limit_up_count !== null" 
                            :class="[table.limit_up_count > 0 ? 'bg-danger' : 'bg-secondary', 'text-white flex-shrink-0']"
                            style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; font-weight: bold; opacity: 0.9;"
                          >
                            {{ table.limit_up_count }} 涨停
                          </span>
                        </div>
                        <span class="topic-time text-muted x-small flex-shrink-0 ml-2">
                          <b-icon icon="clock" class="mr-1"></b-icon>{{ formatTime(table.updated_time) }}
                        </span>
                      </div>
                      <p class="topic-desc mb-0 text-muted small text-truncate-2">
                        {{ table.content || '暂无描述' }}
                      </p>
                    </div>

                    <div class="ml-3 pl-2 border-left d-none d-md-block">
                      <b-button variant="link" class="p-0 text-primary" @click.stop="selectTable(table)">
                        <b-icon icon="chevron-right" font-scale="1.2"></b-icon>
                      </b-button>
                    </div>
                  </div>
                </draggable>

                <!-- 普通列表 -->
                <div 
                  v-for="(table, tIdx) in normalTables" 
                  :key="table.id" 
                  :class="['topic-card mb-3 p-3 d-flex align-items-center cursor-pointer transition-all', table.is_deleted ? 'topic-card-deleted' : '']"
                  @click="onRowClicked(table)"
                >
                  <!-- 装饰性序号 -->
                  <div :class="['topic-rank mr-3', getRankClass(pinnedTables.length + tIdx)]">
                    {{ (currentPage - 1) * perPage + pinnedTables.length + tIdx + 1 }}
                  </div>

                  <div class="flex-grow-1 min-width-0">
                    <div class="d-flex align-items-start justify-content-between mb-1">
                      <div class="d-flex align-items-center overflow-hidden mr-2">
                        <b-badge v-if="table.is_deleted" variant="danger" class="mr-2 px-2 py-1 small shadow-sm">已删除</b-badge>
                        <h5 class="topic-title mb-0 text-truncate font-weight-bold text-primary mr-2 cursor-pointer" @click.stop="showTopicKline(table)">{{ formatTopicName(table.name) }}</h5>
                        <b-icon v-if="canEdit" icon="pencil-square" class="text-muted hover-primary flex-shrink-0 mr-2" @click.stop="editTable(table)"></b-icon>
                        <b-icon v-if="userRoles.includes('admin')" icon="arrow-up-circle" class="text-muted hover-primary flex-shrink-0 ml-1 mr-2" font-scale="1.2" title="置顶" @click.stop="toggleTop(table)"></b-icon>
                        <b-icon v-if="userRoles.includes('admin') && !table.is_deleted" icon="trash" class="text-muted hover-danger flex-shrink-0 mr-2" font-scale="1.1" title="逻辑删除" @click.stop="confirmDeleteTopic(table)"></b-icon>
                        <b-icon v-if="userRoles.includes('admin') && table.is_deleted" icon="arrow-counterclockwise" class="text-warning hover-success flex-shrink-0 mr-2" font-scale="1.2" title="恢复题材" @click.stop="confirmRestoreTopic(table)"></b-icon>

                        <!-- 当日涨跌幅 -->
                        <span 
                          v-if="table.today_pct !== undefined && table.today_pct !== null" 
                          :class="[table.today_pct > 0 ? 'soft-danger' : table.today_pct < 0 ? 'soft-success' : 'bg-secondary text-white', 'mr-1 flex-shrink-0']"
                          style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; font-weight: bold;"
                        >
                          {{ table.today_pct > 0 ? '+' : '' }}{{ table.today_pct.toFixed(2) }}%
                        </span>

                        <!-- 涨跌个股数 -->
                        <span 
                          v-if="table.up_count !== undefined && table.up_count !== null && table.down_count !== undefined && table.down_count !== null"
                          class="mr-1 flex-shrink-0 font-weight-bold"
                          style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; background-color: rgba(128, 128, 128, 0.15); border: 1px solid rgba(128, 128, 128, 0.2);"
                        >
                          <span class="text-danger mr-1">{{ table.up_count }}涨</span>
                          <span class="text-success">{{ table.down_count }}跌</span>
                        </span>

                        <!-- 领涨个数 -->
                        <span 
                          v-if="table.leader_count !== undefined && table.leader_count !== null" 
                          :class="[table.leader_count > 0 ? 'soft-warning' : 'bg-secondary text-white', 'mr-1 flex-shrink-0']"
                          style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; font-weight: bold;"
                        >
                          {{ table.leader_count }} 领涨
                        </span>

                        <!-- 涨停个数 -->
                        <span 
                          v-if="table.limit_up_count !== undefined && table.limit_up_count !== null" 
                          :class="[table.limit_up_count > 0 ? 'bg-danger' : 'bg-secondary', 'text-white flex-shrink-0']"
                          style="font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; font-weight: bold; opacity: 0.9;"
                        >
                          {{ table.limit_up_count }} 涨停
                        </span>
                      </div>
                      <span class="topic-time text-muted x-small flex-shrink-0 ml-2">
                        <b-icon icon="clock" class="mr-1"></b-icon>{{ formatTime(table.updated_time) }}
                      </span>
                    </div>
                    <p class="topic-desc mb-0 text-muted small text-truncate-2">
                      {{ table.content || '暂无描述' }}
                    </p>
                  </div>

                  <div class="ml-3 pl-2 border-left d-none d-md-block">
                    <b-button variant="link" class="p-0 text-primary" @click.stop="selectTable(table)">
                      <b-icon icon="chevron-right" font-scale="1.2"></b-icon>
                    </b-button>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 d-flex flex-wrap justify-content-between align-items-center">
              <div class="d-flex align-items-center mb-2 mb-md-0">
                <b-button variant="light" class="mr-2 px-3 shadow-sm rounded-pill btn-refresh" @click="loadTables(currentPage)" :disabled="loading">
                  <b-icon icon="arrow-clockwise" :animation="loading ? 'spin' : ''"></b-icon> 刷新
                </b-button>
                <b-badge variant="soft-info" class="px-3 py-2 rounded-pill">{{ totalRows }} 个题材库</b-badge>
              </div>
              <b-pagination v-if="totalRows > perPage" v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="right" class="custom-pagination mb-0" @change="loadTables"></b-pagination>
            </div>
          </div>

          <!-- 右侧监控面板 -->
          <transition name="monitor-slide">
            <div v-if="showMonitorPanel" class="col-12 col-lg-3 mt-4 mt-lg-0">
            <div class="topic-monitor-panel shadow-sm rounded-lg overflow-hidden flex-column d-flex h-100 position-relative" :class="theme === 'light' ? 'light-mode' : 'dark-mode'">
              <!-- 顶层提示：订阅引导 (黑金质感卡片) -->
              <transition name="premium-fade">
                <div v-if="showSubPrompt" class="sub-prompt-overlay rounded-lg p-0 overflow-hidden d-flex flex-column align-items-center pt-5">
                   <div class="premium-card p-4 text-center">
                      <div class="premium-badge-icon mb-3">
                        <i class="fas fa-crown"></i>
                      </div>
                      <h5 class="font-weight-bold text-white mb-2">开通策略模拟V2 VIP</h5>
                      <p class="small text-white-50 mb-4 px-3">解锁实时多空博弈流监测，查看模拟策略详情，学习交流。</p>
                      
                      <div class="d-flex flex-column w-100 px-2">
                        <b-button 
                           block 
                           :variant="isModuleVisible ? 'warning' : 'secondary'" 
                           class="premium-btn py-2 mb-2 font-weight-bold shadow" 
                           :disabled="!isModuleVisible"
                           @click="$router.push({ path: '/subscription', query: { module: 'q_hotking_v2' }})"
                         >
                           {{ isModuleVisible ? '立即开通' : '已封闭，不开放' }}
                         </b-button>
                        <b-button block variant="link" class="text-white-50 x-small text-decoration-none" @click="showSubPrompt = false">
                          以后再说
                        </b-button>
                        <b-button block variant="link" class="text-white-50 x-small text-decoration-none mt-n2" @click="()=>{ showSubPrompt = false; showMonitorPanel = false; }">
                          暂时收起侧栏 <b-icon icon="chevron-right" class="ml-1"></b-icon>
                        </b-button>
                      </div>
                   </div>
                </div>
              </transition>

              <div class="monitor-header p-3 d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <b-icon icon="lightning-fill" class="text-warning mr-2 pulse-warning"></b-icon>
                  <span class="font-weight-bold">实时题材监控</span>
                  <b-badge variant="soft-warning" class="ml-2 cursor-pointer premium-strategy-btn px-2 py-1 shadow-sm" @click="$router.push('/wonder/strategy/detail')" style="font-size: 10px; border: 1px solid rgba(255, 193, 7, 0.4); border-radius: 12px; background: rgba(255, 193, 7, 0.15); color: #ffc107; transition: all 0.2s ease;">
                    <i class="fas fa-crown mr-1"></i>策略详情
                  </b-badge>
                </div>
                <div class="d-flex align-items-center">
                  <b-form-checkbox v-model="isMonitorEnabled" switch class="mr-2 custom-switch-sm" @change="toggleMonitor"></b-form-checkbox>
                  <template v-if="userHasMonitorPermission">
                    <b-badge v-if="isMonitorEnabled" :variant="wsStatus === 'connected' ? 'success' : 'danger'" pill class="x-small shadow-sm">
                      <i class="fas fa-circle mr-1" style="font-size: 6px; vertical-align: middle;"></i>
                      {{ wsStatus === 'connected' ? '在线' : '离线' }}
                    </b-badge>
                    <b-badge v-else variant="secondary" pill class="x-small opacity-7">未开启</b-badge>
                  </template>
                  <b-badge v-else-if="isMonitorEnabled" variant="warning" pill class="x-small shadow-sm cursor-pointer" @click="showSubPrompt = true">
                    <i class="fas fa-lock-alt mr-1"></i>需授权
                  </b-badge>
                  <b-icon icon="chevron-right" class="ml-2 text-muted cursor-pointer hover-primary" @click="showMonitorPanel = false" title="隐藏监控"></b-icon>
                </div>
              </div>
              
              <div class="monitor-body flex-grow-1 p-0 position-relative" ref="monitorBody">
                <!-- 无权限遮罩状态 (开启监控但无订阅) -->
                <div v-if="isMonitorEnabled && !userHasPermission" class="locked-overlay d-flex flex-column align-items-center justify-content-center p-4 text-center">
                  <div class="lock-circle mb-3">
                    <i class="fas fa-lock"></i>
                  </div>
                  <p class="text-muted small mb-3">实时监控功能已锁定</p>
                  <b-button size="sm" variant="warning" class="rounded-pill px-4 font-weight-bold" @click="showSubPrompt = true">
                    点击解锁
                  </b-button>
                </div>

                <div class="p-3" :class="{'blur-content': (isMonitorEnabled && !userHasPermission) || showSubPrompt}">
                <div v-if="!isMonitorEnabled" class="empty-monitor text-center py-5">
                  <b-icon icon="power" font-scale="2" class="text-muted mb-3 opacity-3"></b-icon>
                  <p class="text-muted small">实时监控已关闭<br><span class="x-small">请打开上方开关以接收异动信息</span></p>
                </div>
                <div v-else-if="!topicAlerts.length" class="empty-monitor text-center py-5">
                  <b-icon icon="broadcast" font-scale="2" class="text-muted mb-3 opacity-3"></b-icon>
                  <p class="text-muted small">等待信号触发...</p>
                </div>
                
                <transition-group name="alert-bubble" tag="div">
                  <div 
                    v-for="alert in topicAlerts" 
                    :key="alert.id" 
                    class="alert-bubble mb-3 p-2 rounded border-left-3"
                    :class="alert.trigger.pct > 0 ? 'border-danger' : 'border-success'"
                  >
                    <div class="d-flex justify-content-between align-items-start mb-1">
                      <span class="topic-name font-weight-bold text-primary small text-truncate">{{ alert.topic }}</span>
                      <span class="alert-time text-muted x-small">{{ alert.trigger.time }}</span>
                    </div>
                    <div class="stock-info d-flex align-items-center justify-content-between">
                      <div class="d-flex flex-column">
                        <span class="stock-name font-weight-bold small">{{ alert.trigger.name }} ({{ alert.trigger.code }})</span>
                        <span v-if="alert.trigger.reason" class="x-small text-muted">{{ alert.trigger.reason }}<span v-if="alert.trigger.pnl_pct !== undefined"> ({{ (alert.trigger.pnl_pct || 0).toFixed(2) }}%)</span></span>
                        <span v-else-if="alert.trigger.open_pct !== undefined" class="open-pct x-small text-muted">开盘 {{ (alert.trigger.open_pct || 0).toFixed(2) }}%</span>
                      </div>
                      <div class="pct-values d-flex align-items-center">
                        <b-badge 
                          v-if="getAlertScoreStr(alert)" 
                          :class="[getScoreBadgeClass(alert), 'mr-1 x-small font-weight-bold']"
                          :title="getAlertScoreStr(alert)"
                        >
                          评分 {{ getScoreBadgeText(alert) }}
                        </b-badge>
                        <b-badge :variant="(alert.trigger.pct || 0) > 0 ? 'soft-danger' : 'soft-success'" class="mr-1 x-small">
                          {{ (alert.trigger.pct || 0) > 0 ? '+' : '' }}{{ (alert.trigger.pct || 0).toFixed(2) }}%
                        </b-badge>
                      </div>
                    </div>
                    <div v-if="getAlertScoreStr(alert)" class="score-detail-row mt-1 pt-1 border-top d-flex justify-content-between align-items-center x-small">
                      <span class="text-muted">确定性评分:</span>
                      <span class="font-weight-bold" :class="getScoreTextClass(alert)">
                        {{ getAlertScoreStr(alert) }}
                      </span>
                    </div>
                    <div v-if="alert.leader" class="leader-info mt-1 pt-1 border-top border-secondary x-small text-muted" style="white-space: normal;">
                      {{ alert.leader }}
                    </div>
                  </div>
                </transition-group>
              </div>
            </div>
              
            <div class="monitor-footer p-2 bg-dark border-top border-secondary text-center">
                <span class="text-muted x-small">仅展示最新 20 条监控记录</span>
              </div>
            </div>
          </div>
          </transition>
        </div>

        <!-- 侧边栏：创建与识别 -->
        <b-sidebar
          id="sidebar-topic-form"
          v-model="showCreateSidebar"
          right
          shadow
          backdrop
          width="800px"
          title="题材管理"
          header-class="bg-white border-bottom py-3 px-4"
          body-class="bg-light p-4"
        >
          <div class="sidebar-form-container">
            <b-card no-body class="sidebar-card shadow-sm border-0 mb-4" v-if="canEdit">
              <div class="card-header bg-white border-bottom-0 pt-2 px-0">
                <p class="text-muted small mb-0">通过链接或图片快速生成题材表格</p>
              </div>
              <b-card-body class="px-0 pb-4">
                <b-form-group label="快速导入 (韭研公社)" class="mb-4">
                  <b-input-group size="sm" class="custom-input-group">
                    <b-form-input v-model="jygsUrl" placeholder="输入文章链接或ID" class="rounded-left-pill pl-3"></b-form-input>
                    <b-input-group-append>
                      <b-button variant="primary" class="rounded-right-pill px-3" @click="identifyJygs" :disabled="identifying">
                        <b-spinner small v-if="identifying"></b-spinner>
                        <span v-else>识别</span>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>

                <hr class="my-4 opacity-5" />

                <b-form-group label="名称" class="mb-3 font-weight-500">
                  <b-form-input v-model="form.name" placeholder="题材名称" class="custom-input"></b-form-input>
                </b-form-group>

                <div class="row">
                  <div class="col-6">
                    <b-form-group label="创建时间" class="mb-3 small">
                      <b-form-input v-model="form.create_time" size="sm" placeholder="自动获取" class="custom-input-sm"></b-form-input>
                    </b-form-group>
                  </div>
                  <div class="col-6">
                    <b-form-group label="更新时间" class="mb-3 small">
                      <b-form-input v-model="form.update_time" size="sm" placeholder="自动获取" class="custom-input-sm"></b-form-input>
                    </b-form-group>
                  </div>
                </div>

                <b-form-group label="核心内容" class="mb-3 font-weight-500">
                  <b-form-textarea v-model="form.content" rows="3" placeholder="输入题材的核心逻辑或摘要..." class="custom-input"></b-form-textarea>
                </b-form-group>

                <div class="row">
                  <div class="col-6">
                    <b-form-group label="来源" class="mb-3 font-weight-500">
                      <b-form-select v-model="form.source" :options="sourceOptions" class="custom-input"></b-form-select>
                    </b-form-group>
                  </div>
                  <div class="col-6">
                    <b-form-group label="文章ID" class="mb-3 font-weight-500">
                      <b-form-input v-model="form.article_id" placeholder="来源文章ID" class="custom-input"></b-form-input>
                    </b-form-group>
                  </div>
                </div>

                <b-form-group label="题材配图" class="mb-4 font-weight-500">
                  <div class="image-upload-wrapper border rounded-lg p-2 bg-light mb-2">
                    <b-form-file v-model="form.image" accept="image/*" @input="onImageChange" plain class="w-100 x-small"></b-form-file>
                  </div>
                  <b-input-group size="sm" class="custom-input-group">
                    <b-input-group-prepend is-text class="bg-light border-right-0 x-small">URL</b-input-group-prepend>
                    <b-form-input v-model="form.image_url" placeholder="或粘贴图片地址" class="border-left-0 x-small" @input="onImageUrlChange"></b-form-input>
                  </b-input-group>
                </b-form-group>

                <div v-if="imagePreview" class="mb-3 text-center bg-light rounded-lg p-2 overflow-hidden" style="max-height: 150px;">
                  <img :src="imagePreview" alt="预览" class="img-fluid rounded" />
                </div>

                <div class="d-grid gap-2">
                  <b-button block variant="outline-primary" class="rounded-pill mb-2" :disabled="submitting || (!form.image && !form.image_url)" @click="recognizeImage">
                    <b-icon icon="cpu" class="mr-1"></b-icon> 智能识别图片
                  </b-button>
                  <b-button block variant="success" class="rounded-pill" :disabled="submitting || !form.name" @click="createTable">
                    <b-icon icon="plus-circle" class="mr-1"></b-icon> 立即创建题材
                  </b-button>
                </div>

                <b-alert :show="ocrMsg!==''" variant="soft-info" class="mt-3 small py-2 mb-0 border-0">
                  <b-icon icon="info-circle" class="mr-1"></b-icon> {{ ocrMsg }}
                </b-alert>
              </b-card-body>
            </b-card>

            <div v-else class="permission-notice text-center p-4 bg-white rounded-lg shadow-sm">
              <div class="permission-icon-box mb-3 mx-auto">
                <b-icon icon="shield-lock" font-scale="2" class="text-warning"></b-icon>
              </div>
              <h6 class="font-weight-bold mb-2">权限受限</h6>
              <p class="text-muted small mb-3">当前账号仅有查看权限，无法创建或编辑题材表格。</p>
              <b-button variant="soft-info" size="sm" class="rounded-pill px-3" to="/contact">申请权限</b-button>
            </div>

            <!-- 识别结果区 (移入侧边栏) -->
            <div v-if="canEdit && (recognized.length || showRecognitionArea)" class="mt-4 pt-4 border-top">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="font-weight-bold mb-0">识别结果表格</h6>
                <div class="d-flex align-items-center">
                  <b-button size="sm" variant="success" class="mr-2" @click="addRecognizedRow" v-if="recognitionView==='table'">
                    <b-icon icon="plus" class="mr-1"></b-icon> 新增行
                  </b-button>
                  <b-button-group size="sm">
                    <b-button :variant="recognitionView==='table'?'primary':'outline-secondary'" @click="()=>{ if(recognitionView==='json') syncRecognitionJsonToTable(); recognitionView='table' }">表格</b-button>
                    <b-button :variant="recognitionView==='json'?'primary':'outline-secondary'" @click="switchToRecognitionJsonMode">JSON</b-button>
                  </b-button-group>
                </div>
              </div>

              <div class="recognition-table-wrapper rounded-lg border bg-white overflow-hidden shadow-sm">
                <b-table v-if="recognitionView==='table'" small striped hover responsive :items="recognized" :fields="recognizedFields" class="mb-0 text-nowrap">
                  <template #cell(一级大类)="{ item }"><b-form-input v-model="item['一级大类']" size="sm" class="border-0 bg-transparent"></b-form-input></template>
                  <template #cell(二级小类)="{ item }"><b-form-input v-model="item['二级小类']" size="sm" class="border-0 bg-transparent"></b-form-input></template>
                  <template #cell(三级细分)="{ item }"><b-form-input v-model="item['三级细分']" size="sm" class="border-0 bg-transparent"></b-form-input></template>
                  <template #cell(个股)="{ item }"><b-form-input v-model="item['个股']" size="sm" class="border-0 bg-transparent"></b-form-input></template>
                  <template #cell(涨跌幅)="{ item }">
                    <b-form-input v-model="item['涨跌幅']" size="sm" class="border-0 bg-transparent"></b-form-input>
                  </template>
                  <template #cell(相关性)="{ item }"><b-form-input v-model="item['相关性']" size="sm" class="border-0 bg-transparent"></b-form-input></template>
                  <template #cell(信息源)="{ item }"><b-form-input v-model="item['信息源']" size="sm" class="border-0 bg-transparent"></b-form-input></template>
                  <template #cell(actions)="{ index }">
                    <b-button size="sm" variant="link" class="text-danger p-0" @click="recognized.splice(index, 1)">
                      <b-icon icon="trash"></b-icon>
                    </b-button>
                  </template>
                </b-table>
                
                <div v-if="recognitionView==='json'" class="p-2">
                  <b-form-textarea
                    v-model="recognitionJsonStr"
                    placeholder="请输入JSON数据..."
                    rows="15"
                    max-rows="25"
                    class="border-0 bg-light"
                    @blur="syncRecognitionJsonToTable"
                  ></b-form-textarea>
                  <b-alert :show="!!recognitionJsonError" variant="danger" class="mt-2 py-1 small">{{ recognitionJsonError }}</b-alert>
                </div>
              </div>
            </div>
          </div>
        </b-sidebar>



      </b-card-body>
    </b-card>
    <StockInfoSidebar :symbol_code="sidebarSymbolCode" :symbol_name="sidebarSymbolName" :date_mark="sidebarDateMark" />

    <!-- 悬浮监控触发按钮 -->
    <transition name="fade">
      <div v-if="!showMonitorPanel" class="monitor-float-trigger shadow-sm" @click="showMonitorPanel = true" title="显示实时监控">
        <b-icon icon="lightning-fill" class="pulse-warning"></b-icon>
      </div>
    </transition>
    <!-- 题材K线图模态框 -->
    <TopicKlineModal
      :visible="showTopicKlineModal"
      :uniqueKey="selectedTopicUniqueKey"
      :topicName="selectedTopicName"
      @close="showTopicKlineModal = false"
    />
  </div>
</template>

<style scoped>
.topic-monitor-panel {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  height: calc(100vh - 180px);
  min-height: 500px;
  position: sticky;
  top: 20px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}

/* 深色模式适配 */
.topic-monitor-panel.dark-mode {
  background: #0f172a !important; /* 深蓝黑背景 */
  border-color: #1e293b !important;
}

.topic-monitor-panel.dark-mode .monitor-header {
  background: #1e293b !important;
  border-bottom: 1px solid #334155 !important;
  color: #f8fafc !important;
}

.topic-monitor-panel.dark-mode .monitor-body {
  background: #0f172a !important;
}

.topic-monitor-panel.dark-mode .alert-bubble {
  background: rgba(30, 41, 59, 0.7) !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4) !important;
  color: #f1f5f9 !important;
}

.topic-monitor-panel.dark-mode .alert-bubble:hover {
  background: rgba(51, 65, 85, 0.9) !important;
  transform: translateX(-5px);
}

.topic-monitor-panel.dark-mode .stock-name {
  color: #f8fafc !important;
}

.topic-monitor-panel.dark-mode .text-muted,
.topic-monitor-panel.dark-mode .x-small.text-muted {
  color: #94a3b8 !important;
}

.topic-monitor-panel.dark-mode .monitor-footer {
  background: #0f172a !important;
  border-top: 1px solid #1e293b !important;
}

.monitor-header {
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-muted);
  color: var(--text-main);
}

.monitor-body {
  overflow-y: auto;
  background-color: var(--bg-main);
  flex-grow: 1;
}

.alert-bubble {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-left: 4px solid transparent;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--text-main);
}

.alert-bubble:hover {
  transform: translateX(-5px);
  background: var(--bg-muted);
}

.topic-monitor-panel.dark-mode .topic-name {
  color: #38bdf8 !important; /* Sky 400 */
}

.stock-name {
  color: var(--text-main);
  font-weight: bold;
}

.leader-info {
  border-top: 1px solid var(--border-color);
  color: var(--text-muted) !important;
}

.topic-name {
  color: var(--accent-color) !important;
}

.custom-switch-sm.custom-control.custom-switch .custom-control-label::before {
  height: 1rem;
  width: 1.75rem;
  border-radius: 0.5rem;
}
.custom-switch-sm.custom-control.custom-switch .custom-control-label::after {
  width: calc(1rem - 4px);
  height: calc(1rem - 4px);
  border-radius: 0.5rem;
}
.custom-switch-sm.custom-control.custom-switch .custom-control-input:checked ~ .custom-control-label::after {
  transform: translateX(0.75rem);
}

/* 共有样式与动画 */
.cursor-move { cursor: move !important; }
.border-danger { border-left-color: var(--color-up-color) !important; }
.border-success { border-left-color: var(--color-down-color) !important; }

.x-small { font-size: 0.7rem; }
.soft-danger { background-color: rgba(236, 0, 0, 0.1); color: var(--color-up-color); }
.soft-success { background-color: rgba(0, 218, 60, 0.1); color: var(--color-down-color); }
.soft-warning { background-color: rgba(255, 165, 0, 0.15); color: #ff9800; }
.soft-info,
.badge-soft-info { background-color: rgba(14, 165, 233, 0.15); color: #0284c7; }
.soft-secondary,
.badge-soft-secondary { background-color: rgba(148, 163, 184, 0.15); color: #64748b; }

.premium-strategy-btn:hover {
  transform: translateY(-1px) scale(1.05);
  background-color: rgba(255, 193, 7, 0.25) !important;
  box-shadow: 0 4px 8px rgba(255, 193, 7, 0.3) !important;
  text-decoration: none;
}

/* 深色模式下的软色调 badge */
.dark-mode .soft-danger {
  background-color: rgba(248, 113, 113, 0.15) !important;
  color: #f87171 !important;
}
.dark-mode .soft-success {
  background-color: rgba(74, 222, 128, 0.15) !important;
  color: #4ade80 !important;
}
.dark-mode .soft-warning {
  background-color: rgba(251, 191, 36, 0.2) !important;
  color: #fbbf24 !important;
}
.dark-mode .soft-info,
.dark-mode .badge-soft-info {
  background-color: rgba(56, 189, 248, 0.2) !important;
  color: #38bdf8 !important;
}
.dark-mode .soft-secondary,
.dark-mode .badge-soft-secondary {
  background-color: rgba(148, 163, 184, 0.2) !important;
  color: #94a3b8 !important;
}

.score-detail-row {
  border-top: 1px solid var(--border-color) !important;
}

.topic-monitor-panel.dark-mode .score-detail-row {
  border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.pulse-warning {
  animation: pulse-ring 2s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
}

@keyframes pulse-ring {
  0% { transform: scale(0.8); opacity: 0.5; }
  50% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.8); opacity: 0.5; }
}

/* 消息入场动画 */
.alert-bubble-enter-active {
  transition: all 0.5s ease;
}
.alert-bubble-enter {
  opacity: 0;
  transform: translateX(30px);
}
.alert-bubble-move {
  transition: transform 0.5s;
}
</style>

<script>
import { request_zzquant } from 'network/request.js'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'
import draggable from 'vuedraggable'
import TopicKlineModal from '@/components/plate/TopicKlineModal.vue'

export default {
  components: { StockInfoSidebar, draggable, TopicKlineModal },
  data(){
    return {
      windowWidth: window.innerWidth,
      theme: 'light',
      showTopicKlineModal: false,
      selectedTopicUniqueKey: '',
      selectedTopicName: '',
      tables: [],
      tableFields: [ 
        { key:'updated_time', label:'更新时间', sortable:true, class: 'text-center align-middle d-none d-md-table-cell', thClass: 'text-center d-none d-md-table-cell', thStyle: { width: '150px' } }, 
        { key:'name', label:'名称', sortable:true, class: 'align-middle', thStyle: { width: '30%' } }, 
        { key:'content', label:'内容', class: 'align-middle', thStyle: { width: '40%' } },
        { key:'created_time', label:'创建时间', sortable:true, class: 'text-center align-middle d-none d-md-table-cell', thClass: 'd-none d-md-table-cell', thStyle: { width: '150px' } }, 
        { key:'actions', label:'详情', class: 'text-right align-middle', thClass: 'text-right', thStyle: { width: '120px' } } 
      ],

      loading: false,
      submitting: false,
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      jygsUrl: '',
      identifying: false,
      form: { name:'', content:'', source: 1, article_id: '', image:null, image_url:'', create_time:'', update_time:'' },
      sourceOptions: [
        { value: 1, text: '韭研公社' },
        { value: 0, text: '其他' }
      ],
      imagePreview: '',
      recognized: [],
      ocrMsg: '',
      recognizedFields: [ { key:'一级大类', label:'一级大类' }, { key:'二级小类', label:'二级小类' }, { key:'三级细分', label:'三级细分' }, { key:'个股', label:'个股' }, { key:'相关性', label:'相关性' }, { key:'信息源', label:'信息源' }, { key:'actions', label:'详情' } ],
      recognitionView: 'table',
      recognitionJsonStr: '',
      recognitionJsonError: '',
      showRecognitionArea: false,
      showEdit: false,
      editId: null,
      editForm: { name:'', content:'', rows:[], image: null, created_time: '', updated_time: '' },
      editImagePreview: '',
      editOcrMsg: '',
      isEditing: false,
      viewMode: 'table',
      mindMapOption: {},
      mindMapStyle: 'LR',
      mindMapCurve: 'curve',
      mindMapSymbol: 'emptyCircle',
      loadingStocks: false,
      jsonStr: '',
      jsonError: '',
      searchTimer: null,
      lastQuoteTime: '',
      contentCollapsed: true,
      showQuotes: false,
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      sidebarDateMark: '',

      // 题材监控
      isMonitorEnabled: false,
      userSubscriptions: [], // 用户订阅列表
      showSubPrompt: false, // 是否显示订阅提示层
      isModuleVisible: true, // 策略模拟V2模块是否开放

      // 强度排名与历史回溯
      sortMode: 'default', // 'default' | 'strength'
      strengthSort: 'strength', // 'strength' | 'score_b' | 'score_a'
      selectedRankDate: '', // 选中的历史交易日 'YYYY-MM-DD'
      currentTradeDate: '', // 后端实际返回的交易日
      
      searchQuery: '',
      allTables: [],
      showCreateSidebar: false,
      userRoles: [],
      // WebSocket 相关
      ws: null,
      wsStatus: 'disconnected',
      topicAlerts: [],
      maxAlerts: 20,
      showMonitorPanel: true
    }
  },
  computed:{
    todayStr() {
      const d = new Date()
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    isLatestDate() {
      if (!this.selectedRankDate) return true
      return this.selectedRankDate >= this.todayStr
    },
    pinnedTables: {
      get() {
        return this.tables.filter(t => t.is_top === 1)
      },
      set(value) {
        const normals = this.tables.filter(t => t.is_top !== 1)
        this.tables = [...value, ...normals]
      }
    },
    normalTables() {
      return this.tables.filter(t => t.is_top !== 1)
    },
    isMobile() {
      return this.windowWidth < 768
    },
    isAdmin() {
      if (this.userRoles && (this.userRoles.includes('admin') || this.userRoles.includes('editor'))) {
        return true
      }
      try {
        const raw = localStorage.getItem('user_info')
        if (raw) {
          const u = JSON.parse(raw)
          const r = u && u.roles
          const arr = Array.isArray(r) ? r : String(r || '').split(',').map(s => s.trim()).filter(Boolean)
          if (arr.includes('admin') || arr.includes('editor')) return true
        }
      } catch (e) {
        console.warn('isAdmin check error', e)
      }
      return false
    },
    userHasPermission() {
      // 后端返回的 feature_subscriptions 已经是经过 status=1 和过期时间过滤的活跃订阅
      const hasSub = this.userSubscriptions && this.userSubscriptions.some(s => s.code === 'topic_kline')
      return this.isAdmin || hasSub
    },
    userHasMonitorPermission() {
      const hasSub = this.userSubscriptions && this.userSubscriptions.some(s => s.code === 'q_hotking_v2')
      return this.isAdmin || hasSub
    },
    canEdit() {
      return this.userRoles.includes('post') || this.userRoles.includes('admin')
    },
    modalTableFields(){
      try{
        const base = Array.isArray(this.recognizedFields) ? this.recognizedFields : []
        let fields = base.slice()
        if(!this.showQuotes){
          fields = fields.filter(f => f.key !== '流通市值' && f.key !== '最新价' && f.key !== '涨跌幅')
        } else {
          const hasCap = fields.some(f => f.key === '流通市值')
          const hasPrice = fields.some(f => f.key === '最新价')
          const hasRate = fields.some(f => f.key === '涨跌幅')
          if(!hasCap) fields.splice(4, 0, { key:'流通市值', label:'流通市值' })
          if(!hasPrice) fields.splice(5, 0, { key:'最新价', label:'当前价格' })
          if(!hasRate) fields.splice(6, 0, { key:'涨跌幅', label:'涨跌幅' })
          const hasCode = fields.some(f => f.key === 'symbol_code')
          if(!hasCode) fields.splice(4, 0, { key:'symbol_code', label:'股票代码' })
        }
        if(this.isEditing) return fields
        let filtered = fields.filter(f => f.key !== 'actions')
        if (!this.canEdit) {
          filtered = filtered.filter(f => f.key !== '信息源')
        }
        return filtered
      }catch(e){
        return (this.recognizedFields || []).filter(f => f.key !== 'actions')
      }
    },
    mergedRows(){
      try{
        const src = Array.isArray(this.editForm.rows) ? this.editForm.rows : []
        const rows = src.map(r => ({
          ...r,
          _l1Show: true, _l1Span: 1,
          _l2Show: true, _l2Span: 1,
          _l3Show: true, _l3Span: 1
        }))
        const n = rows.length
        let i = 0
        while (i < n) {
          const l1 = (rows[i]['一级大类'] || '').trim()
          let j = i + 1
          while (j < n && (rows[j]['一级大类'] || '').trim() === l1) j++
          rows[i]._l1Show = true
          rows[i]._l1Span = j - i
          for (let k = i + 1; k < j; k++) { rows[k]._l1Show = false }
          let k = i
          while (k < j) {
            const l2 = (rows[k]['二级小类'] || '').trim()
            let m = k + 1
            while (m < j && (rows[m]['二级小类'] || '').trim() === l2) m++
            rows[k]._l2Show = true
            rows[k]._l2Span = m - k
            for (let t = k + 1; t < m; t++) { rows[t]._l2Show = false }
            let t = k
            while (t < m) {
              const l3 = (rows[t]['三级细分'] || '').trim()
              let u = t + 1
              while (u < m && (rows[m]['三级细分'] || '').trim() === l3) u++
              rows[t]._l3Show = true
              rows[t]._l3Span = u - t
              for (let v = t + 1; v < u; v++) { rows[v]._l3Show = false }
              t = u
            }
            k = m
          }
          i = j
        }
        return rows
      }catch(e){
        return this.editForm.rows || []
      }
    }
  },
  mounted(){ 
    try {
      const q = sessionStorage.getItem('topic_search_query')
      if (q !== null) this.searchQuery = q
      const p = sessionStorage.getItem('topic_search_page')
      if (p !== null) this.currentPage = parseInt(p, 10) || 1
      const sm = this.$route && this.$route.query ? this.$route.query.sort_mode : null
      if (sm === 'strength') this.sortMode = 'strength'
      const td = this.$route && this.$route.query ? this.$route.query.trade_date : null
      if (td) this.selectedRankDate = td
    } catch (e) {
      console.warn('Failed to restore search query/page from sessionStorage', e)
    }
    this.loadTables(this.currentPage)
    window.addEventListener('paste', this.handlePaste)
    window.addEventListener('resize', this.onResize)
    
    try{ const t = localStorage.getItem('site.theme'); if(t){ this.theme = t } }catch(e){ console.warn('theme read failed', e) }
    this.onThemeChanged = (e)=>{ const theme = e && e.detail && e.detail.theme ? e.detail.theme : (localStorage.getItem('site.theme')||'light'); this.theme = theme; this.generateMindMap() }
    window.addEventListener('site-theme-changed', this.onThemeChanged)
    
    this.updateUserRoles()
    this.fetchUserSubscriptions()
    this.checkModuleVisibility()
    this.onProfileUpdated = (e) => {
      const u = e && e.detail ? e.detail : {}
      const r = u.roles
      this.userRoles = Array.isArray(r) ? r : String(r||'').split(',').map(s=>s.trim()).filter(Boolean)
    }
    window.addEventListener('user-profile-updated', this.onProfileUpdated)
  },
  beforeDestroy() {
    window.removeEventListener('paste', this.handlePaste)
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('site-theme-changed', this.onThemeChanged)
    window.removeEventListener('user-profile-updated', this.onProfileUpdated)
    this.closeWebSocket()
  },
  methods:{
    async fetchUserSubscriptions() {
      try {
        const res = await request_zzquant({ url: '/v3/user/subscriptions', method: 'get' })
        if (res.data && res.data.code === 20000) {
          this.userSubscriptions = res.data.data.feature_subscriptions || []
        }
      } catch (error) { console.error('获取用户订阅失败', error) }
    },
    async checkModuleVisibility() {
      try {
        const res = await request_zzquant({ url: '/v3/payment/modules', method: 'get' })
        if (res.data && res.data.code === 20000) {
          const list = res.data.data || []
          this.isModuleVisible = list.some(m => m.code === 'q_hotking_v2')
        }
      } catch (error) {
        console.error('获取订阅模块列表失败', error)
      }
    },
    // WebSocket 逻辑
    async toggleMonitor(enabled) {
      if (enabled) {
        // 如果没有权限，弹出订阅引导卡片并保持开关关闭
        if (!this.userHasMonitorPermission) {
          this.showSubPrompt = true
          this.$nextTick(() => { this.isMonitorEnabled = false }) 
          return
        }
        this.initWebSocket()
      } else {
        this.closeWebSocket()
        this.topicAlerts = []
        this.showSubPrompt = false
      }
    },
    initWebSocket() {
      if (!this.isMonitorEnabled || this.ws) return
      
      const token = localStorage.getItem('token')
      if (!token) {
        this.$bvToast.toast('请先登录', { variant: 'danger' })
        this.isMonitorEnabled = false
        return
      }

      let wsUrl = `wss://api.zizizaizai.com/v3/ws/topic_monitor?token=${token}`
      console.log('Connecting to Topic Monitor WebSocket with token:', wsUrl.substring(0, 60) + '...')
      this.ws = new WebSocket(wsUrl)
      this.ws.onopen = () => { 
        this.wsStatus = 'connected'
        console.log('Topic Monitor WebSocket connected successfully')
      }
      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          // 处理后端返回的错误消息
          if (data.type === 'error') {
            this.$bvToast.toast(data.message, { title: '监控提示', variant: 'danger', solid: true })
            this.isMonitorEnabled = false
            return
          }
          console.log('WS Received Message:', data.type, data)
          if (data.type === 'topic_alert') { 
            console.log('Received Topic Alert:', data.topic)
            this.handleTopicAlert(data) 
          }
        } catch (e) { console.error('Error parsing WS message:', e) }
      }
      this.ws.onclose = (e) => {
        this.wsStatus = 'disconnected'
        this.ws = null
        if (this.isMonitorEnabled) {
          console.warn(`Topic Monitor WebSocket closed: code=${e.code}, reason=${e.reason}. Retrying in 5s...`)
          setTimeout(() => this.initWebSocket(), 5000)
        }
      }
      this.ws.onerror = (err) => { 
        this.wsStatus = 'disconnected'
        console.error('Topic Monitor WebSocket error observed:', err)
      }
    },
    handleTopicAlert(alert) {
      if (!alert || !alert.trigger) return
      const newAlert = { ...alert, id: Date.now() + Math.random() }
      console.log('Adding Alert to List:', newAlert.topic)
      this.topicAlerts.unshift(newAlert)
      if (this.topicAlerts.length > this.maxAlerts) {
        this.topicAlerts = this.topicAlerts.slice(0, this.maxAlerts)
      }
    },
    getAlertScore(alert) {
      if (!alert) return null
      const trigger = alert.trigger || {}
      if (trigger.score !== undefined && trigger.score !== null) {
        const num = Number(trigger.score)
        if (!isNaN(num)) return num
      }
      if (alert.score !== undefined && alert.score !== null) {
        const num = Number(alert.score)
        if (!isNaN(num)) return num
      }
      const str = trigger.score_str || alert.score_str
      if (typeof str === 'string') {
        const match = str.trim().match(/^([+-]?\d+)/)
        if (match) {
          return parseInt(match[1], 10)
        }
      }
      return null
    },
    getAlertScoreStr(alert) {
      if (!alert) return ''
      const trigger = alert.trigger || {}
      return trigger.score_str || alert.score_str || ''
    },
    getScoreBadgeClass(alert) {
      const score = this.getAlertScore(alert)
      if (score === null) return 'soft-secondary'
      if (score >= 2) return 'soft-danger'
      if (score >= 1) return 'soft-warning'
      if (score === 0) return 'soft-info'
      return 'soft-secondary'
    },
    getScoreBadgeText(alert) {
      const score = this.getAlertScore(alert)
      if (score !== null) {
        return score > 0 ? `+${score}` : `${score}`
      }
      const str = this.getAlertScoreStr(alert)
      if (str) {
        const firstPart = str.split(' ')[0]
        return firstPart.length <= 4 ? firstPart : '分'
      }
      return ''
    },
    getScoreTextClass(alert) {
      const score = this.getAlertScore(alert)
      if (score === null) return 'text-muted'
      if (score >= 2) return 'text-danger'
      if (score >= 1) return 'text-warning'
      if (score === 0) return 'text-info'
      return 'text-muted'
    },
    closeWebSocket() {
      if (this.ws) { this.ws.close(); this.ws = null }
    },
    triggerMonitor() {
      request_zzquant({ url: '/v3/topic/monitor/trigger', method: 'POST' })
        .then(res => {
          const d = res.data || {}
          if (d.code === 20000) {
            this.$bvToast.toast(d.msg || '触发成功', { title: '监控管理', variant: 'success', solid: true })
          } else {
            this.$bvToast.toast(d.msg || '触发失败', { title: '监控管理', variant: 'danger', solid: true })
          }
        })
        .catch(err => this.$bvToast.toast(err.message || '请求失败', { variant: 'danger', solid: true }))
    },
    refreshMonitor() {
      request_zzquant({ url: '/v3/topic/monitor/refresh', method: 'POST' })
        .then(res => {
          const d = res.data || {}
          if (d.code === 20000) {
            this.$bvToast.toast(d.msg || '刷新成功', { title: '监控管理', variant: 'success', solid: true })
          } else {
            this.$bvToast.toast(d.msg || '刷新失败', { title: '监控管理', variant: 'danger', solid: true })
          }
        })
        .catch(err => this.$bvToast.toast(err.message || '请求失败', { variant: 'danger', solid: true }))
    },
    onResize() {
      this.windowWidth = window.innerWidth
    },
    updateUserRoles() {
      try {
        const raw = localStorage.getItem('user_info')
        if(!raw) { this.userRoles = []; return }
        const u = JSON.parse(raw)
        const r = u && u.roles
        this.userRoles = Array.isArray(r) ? r : String(r||'').split(',').map(s=>s.trim()).filter(Boolean)
      } catch(e) { this.userRoles = [] }
    },
    formatTime(t) {
      if(!t) return ''
      // t is likely "YYYY-MM-DD HH:mm:ss"
      if (typeof t === 'string' && t.length >= 16) {
        if (this.isMobile) {
          return t.substring(5, 16) // MM-DD HH:mm
        }
        return t.substring(0, 16) // YYYY-MM-DD HH:mm
      }
      return t
    },
    formatTopicName(name) {
      if (!name) return ''
      return name.replace(/\(\d{6,8}\)$/, '')
    },
    getSourceLabel(s) {
      if(s === 1) return '韭研公社'
      return '其他'
    },
    formatTimeDisplay(val) {
      if (!val) return ''
      if (this.isMobile && typeof val === 'string' && val.length >= 16) {
        // Assume format YYYY-MM-DD HH:mm:ss, remove YYYY- and :ss
        // Returns MM-DD HH:mm
        return val.substring(5, 16)
      }
      return val
    },
    formatYi(val){
      if (val === undefined || val === null || val === '') return ''
      const n = parseFloat(val)
      if (isNaN(n)) return String(val)
      const yi = n / 100000000
      return yi.toFixed(2) + '亿'
    },
    downloadTableImage(){
      try{
        const showQ = this.showQuotes
        const header = showQ 
          ? ['一级大类','二级小类','三级细分','个股','股票代码','流通市值','当前价格','涨跌幅','相关性']
          : ['一级大类','二级小类','三级细分','个股','相关性']
        const colWidths = showQ 
          ? [140,140,160,160,120,130,110,90,320]
          : [140,140,160,180,320]
        
        if (this.canEdit) {
          header.push('信息源')
          colWidths.push(220)
        }
        const paddingX = 10
        const paddingY = 8
        const font = '12px sans-serif'
        const rows = Array.isArray(this.mergedRows) ? this.mergedRows : []
        const ctx = document.createElement('canvas').getContext('2d')
        ctx.font = font
        const wrap = (text, maxWidth) => {
          const t = String(text || '')
          const words = t.split(/\s+/)
          const lines = []
          let line = ''
          const pushLine = (s) => { lines.push(s) }
          const measure = (s) => ctx.measureText(s).width
          if (!words.length) return ['']
          for (let i=0;i<words.length;i++){
            const w = words[i]
            const test = line ? (line + ' ' + w) : w
            if (measure(test) + paddingX*2 <= maxWidth) {
              line = test
            } else {
              if (line) pushLine(line)
              if (measure(w) + paddingX*2 <= maxWidth) {
                line = w
              } else {
                let part = ''
                for (let j=0;j<w.length;j++){
                  const test2 = part + w[j]
                  if (measure(test2) + paddingX*2 <= maxWidth) part = test2
                  else { if (part) pushLine(part); part = w[j] }
                }
                line = part
              }
            }
          }
          if (line) pushLine(line)
          const byNewline = t.split('\n')
          if (byNewline.length > 1) return t.split('\n')
          return lines.length ? lines : [t]
        }
        const colCount = header.length
        const IDX = {
          L1: 0, L2: 1, L3: 2, STOCK: 3,
          CODE: showQ ? 4 : -1,
          CAP: showQ ? 5 : -1,
          PRICE: showQ ? 6 : -1,
          RATE: showQ ? 7 : -1,
          REL: showQ ? 8 : 4,
          SRC: showQ ? 9 : 5
        }
        const tableWidth = colWidths.reduce((a,b)=>a+b,0)
        const rowBaseHeight = 26
        const rowHeights = []
        for (let i=0;i<rows.length;i++){
          const r = rows[i]
          const relLines = wrap(r['相关性'], colWidths[IDX.REL])
          let srcLines = ['']
          if (this.canEdit) {
            srcLines = wrap(r['信息源'], colWidths[IDX.SRC])
          }
          const lh = Math.max(relLines.length, srcLines.length, 1)
          rowHeights.push(Math.max(rowBaseHeight, lh * 18))
        }
        let totalHeight = 40
        for (let i=0;i<rowHeights.length;i++) totalHeight += rowHeights[i]
        totalHeight += 40
        const canvas = document.createElement('canvas')
        canvas.width = tableWidth + 2
        canvas.height = totalHeight
        const c = canvas.getContext('2d')
        c.fillStyle = '#ffffff'
        c.fillRect(0,0,canvas.width,canvas.height)
        c.save()
        c.globalAlpha = 0.08
        c.fillStyle = '#808080'
        c.textAlign = 'center'
        c.textBaseline = 'middle'
        c.font = 'bold 50px sans-serif'
        c.fillText('自在量化', canvas.width/2, canvas.height/2 - 20)
        c.font = '24px sans-serif'
        c.fillText('quant.zizizaizai.com', canvas.width/2, canvas.height/2 + 20)
        c.restore()
        c.strokeStyle = '#e0e0e0'
        c.lineWidth = 1
        c.fillStyle = '#333333'
        c.font = 'bold 13px sans-serif'
        let x = 0
        let y = 0
        for (let ci=0;ci<colCount;ci++){
          c.fillStyle = '#f5f5f5'
          c.fillRect(x, y, colWidths[ci], 40)
          c.strokeRect(x, y, colWidths[ci], 40)
          c.fillStyle = '#333333'
          c.textAlign = 'left'
          c.textBaseline = 'middle'
          c.fillText(header[ci], x + paddingX, y + 20)
          x += colWidths[ci]
        }
        y += 40
        c.font = font
        const colX = []
        x = 0
        for (let ci=0;ci<colCount;ci++){ colX.push(x); x += colWidths[ci] }
        const groupHeightSum = (start, span) => {
          let h = 0
          for (let k=0;k<span;k++) h += rowHeights[start+k]
          return h
        }
        for (let ri=0;ri<rows.length;ri++){
          const r = rows[ri]
          const h = rowHeights[ri]
          for (let ci=IDX.STOCK;ci<colCount;ci++){
            const cx = colX[ci]
            c.strokeRect(cx, y, colWidths[ci], h)
          }
          const drawMerged = (ci, showFlag, spanProp, textKey) => {
            if (!r[showFlag]) return
            const cx = colX[ci]
            const span = r[spanProp] || 1
            const mh = groupHeightSum(ri, span)
            c.strokeRect(cx, y, colWidths[ci], mh)
            c.textAlign = 'left'
            c.textBaseline = 'top'
            c.fillStyle = '#333333'
            const prevFont = c.font
            c.font = 'bold 12px sans-serif'
            const lines = wrap(r[textKey], colWidths[ci])
            let ty = y + paddingY
            for (let li=0;li<lines.length && li<10;li++){
              c.fillText(lines[li], cx + paddingX, ty)
              ty += 16
            }
            c.font = prevFont
          }
          drawMerged(IDX.L1, '_l1Show', '_l1Span', '一级大类')
          drawMerged(IDX.L2, '_l2Show', '_l2Span', '二级小类')
          drawMerged(IDX.L3, '_l3Show', '_l3Span', '三级细分')
          c.textAlign = 'left'
          c.textBaseline = 'top'
          c.fillStyle = '#333333'
          const stock = String(r['个股'] || '')
          const prevFont2 = c.font
          c.font = 'bold 12px sans-serif'
          c.fillText(stock, colX[IDX.STOCK] + paddingX, y + paddingY)
          c.font = prevFont2
          if (showQ && IDX.CODE >= 0) {
            c.fillStyle = '#333333'
            const code = r.symbol_code || r['股票代码'] || ''
            c.fillText(String(code), colX[IDX.CODE] + paddingX, y + paddingY)
          }
          if (showQ && IDX.CAP >= 0) {
            c.fillStyle = '#333333'
            const cap = r['流通市值']
            const capStr = (cap !== undefined && cap !== null && cap !== '') ? this.formatYi(cap) : ''
            c.fillText(capStr, colX[IDX.CAP] + paddingX, y + paddingY)
          }
          if (showQ && IDX.PRICE >= 0) {
            c.fillStyle = '#333333'
            const price = r['最新价']
            const priceStr = (price !== undefined && price !== null && price !== '') ? String(price) : ''
            c.fillText(priceStr, colX[IDX.PRICE] + paddingX, y + paddingY)
          }
          if (showQ && IDX.RATE >= 0) {
            const rate = r['涨跌幅']
            let rateStr = ''
            if (rate !== undefined && rate !== null && rate !== '') {
              rateStr = String(rate) + '%'
              const rv = parseFloat(rate)
              if (!isNaN(rv)) {
                if (rv > 0) c.fillStyle = '#e33e33'
                else if (rv < 0) c.fillStyle = '#17803d'
                else c.fillStyle = '#333333'
              } else c.fillStyle = '#333333'
            } else c.fillStyle = '#333333'
            c.fillText(rateStr, colX[IDX.RATE] + paddingX, y + paddingY)
          }
          c.fillStyle = '#333333'
          const relLines = wrap(r['相关性'], colWidths[IDX.REL])
          let ty1 = y + paddingY
          for (let li=0;li<relLines.length && li<40;li++){
            c.fillText(relLines[li], colX[IDX.REL] + paddingX, ty1)
            ty1 += 16
          }
          if (this.canEdit) {
            const srcLines = wrap(r['信息源'], colWidths[IDX.SRC])
            let ty2 = y + paddingY
            for (let li=0;li<srcLines.length && li<40;li++){
              c.fillText(srcLines[li], colX[IDX.SRC] + paddingX, ty2)
              ty2 += 16
            }
          }
          y += h
        }
        const a = document.createElement('a')
        const name = (this.editForm.name || '题材表格')
        const ts = this.lastQuoteTime || this.editForm.updated_time || this.formatNow()
        a.href = canvas.toDataURL('image/png')
        a.download = `${name}-${ts}.png`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }catch(e){
        alert('生成图片失败')
        console.error(e)
      }
    },
    downloadTableCsv(){
      const rows = Array.isArray(this.editForm.rows) ? this.editForm.rows : []
      const showQ = this.showQuotes
      const header = showQ 
        ? ['一级大类','二级小类','三级细分','个股','股票代码','流通市值','当前价格','涨跌幅','相关性']
        : ['一级大类','二级小类','三级细分','个股','相关性']
      
      if (this.canEdit) {
        header.push('信息源')
      }
      const lines = [header.join(',')]
      rows.forEach(row => {
        const cap = showQ ? this.formatYi(row['流通市值']) : ''
        const price = showQ ? (row['最新价'] !== undefined && row['最新价'] !== null ? row['最新价'] : '') : ''
        const rate = showQ && row['涨跌幅'] !== undefined && row['涨跌幅'] !== null ? `${row['涨跌幅']}%` : ''
        const code = showQ ? (row.symbol_code || row['股票代码'] || '') : ''
        const vals = showQ
          ? [
            row['一级大类'] || '',
            row['二级小类'] || '',
            row['三级细分'] || '',
            row['个股'] || '',
            code,
            cap,
            price,
            rate,
            row['相关性'] || ''
          ]
          : [
            row['一级大类'] || '',
            row['二级小类'] || '',
            row['三级细分'] || '',
            row['个股'] || '',
            row['相关性'] || ''
          ]
        
        if (this.canEdit) {
          vals.push(row['信息源'] || '')
        }
        const escaped = vals.map(v => `"${String(v).replace(/"/g, '""')}"`)
        lines.push(escaped.join(','))
      })
      const csv = '\uFEFF' + lines.join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const name = this.editForm.name || '题材表格'
      const ts = this.lastQuoteTime || this.editForm.updated_time || this.formatNow()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${name}-${ts}.csv`
      link.click()
      URL.revokeObjectURL(link.href)
    },
    compressImage(file){
      return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = (e) => {
              const img = new Image()
              img.src = e.target.result
              img.onload = () => {
                  const canvas = document.createElement('canvas')
                  let width = img.width
                  let height = img.height
                  // Max dimension 2048 to be safe and reduce size
                  const MAX_DIM = 2048
                  if(width > MAX_DIM || height > MAX_DIM){
                      if(width > height){
                          height = Math.round(height * MAX_DIM / width)
                          width = MAX_DIM
                      } else {
                          width = Math.round(width * MAX_DIM / height)
                          height = MAX_DIM
                      }
                  }
                  canvas.width = width
                  canvas.height = height
                  const ctx = canvas.getContext('2d')
                  ctx.drawImage(img, 0, 0, width, height)
                  canvas.toBlob((blob) => {
                      if(blob){
                          const newFile = new File([blob], file.name || 'image.jpg', { type: 'image/jpeg', lastModified: Date.now() })
                          resolve(newFile)
                      } else {
                          reject(new Error('Canvas toBlob failed'))
                      }
                  }, 'image/jpeg', 0.7)
              }
              img.onerror = reject
          }
          reader.onerror = reject
      })
    },
    identifyJygs(){
      if(!this.jygsUrl) return
      if(this.jygsUrl.indexOf('jiuyangongshe.com') === -1){
        alert('请输入有效的韭研公社链接')
        return
      }
      this.identifying = true
      request_zzquant({ url:'/v3/topic/jygs/parse', method:'POST', data:{ url: this.jygsUrl } })
        .then(res=>{
          const d = res.data
          if((d.code === 20000 || d.code === 200) && d.data){
             const info = d.data
             this.form.name = info.title || ''
             this.form.content = info.content || ''
             this.form.source = info.source || ''
             this.form.article_id = info.article_id || ''
             if(info.create_time) this.$set(this.form, 'create_time', info.create_time)
             if(info.update_time) this.$set(this.form, 'update_time', info.update_time)
             
             let imgUrl = ''
             if(info.images && Array.isArray(info.images) && info.images.length > 0){
                imgUrl = info.images[0]
             } else if (typeof info.images === 'string' && info.images) {
                imgUrl = info.images
             } else if (info.image_url) {
                imgUrl = info.image_url
             } else if (info.imgs) {
                let imgs = info.imgs
                if (typeof imgs === 'string') {
                    try {
                        // Clean up potential markdown or weird chars if needed, though JSON.parse is strict
                        // If it is "[\"url\"]", JSON.parse returns ["url"]
                        const parsed = JSON.parse(imgs)
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            imgUrl = parsed[0]
                        } else if (typeof parsed === 'string') {
                            imgUrl = parsed
                        }
                    } catch (e) {
                        // Fallback: remove brackets/quotes manually if parse fails or it's just a string
                        let s = imgs.replace(/^[["']+|[\]"']+$/g, '')
                        if(s.indexOf(',') > -1) s = s.split(',')[0]
                        imgUrl = s.trim()
                    }
                } else if (Array.isArray(imgs) && imgs.length > 0) {
                     imgUrl = imgs[0]
                }
             }

             if(imgUrl){
                // Remove potential backticks or extra quotes if parsing was messy
                imgUrl = imgUrl.replace(/[`]/g, '').trim()
                this.form.image = null
                this.$set(this.form, 'image_url', imgUrl)
                this.imagePreview = imgUrl
                // this.recognizeImage()
             }
             this.showRecognitionArea = true
             this.switchToRecognitionJsonMode()
             this.$bvToast.toast('获取成功', { variant:'success', solid:true })
          } else {
             alert(d.message || '识别失败')
          }
        })
        .catch(err=>{ console.error(err); alert('请求失败') })
        .finally(()=>{ this.identifying = false })
    },
    handlePaste(event){
      const items = (event.clipboardData || event.originalEvent.clipboardData).items
      for (let index in items) {
        const item = items[index]
        if (item.kind === 'file' && item.type.indexOf('image/') !== -1) {
          const blob = item.getAsFile()
          if (this.showEdit) {
             this.editForm.image = blob
             this.onEditImageChange(blob)
             // Auto recognize if desired, or just show preview
          } else {
             this.form.image = blob
             this.onImageChange(blob)
          }
          event.preventDefault()
          return
        }
      }
    },
    onImageChange(file){ 
      if (file) { 
        try { this.imagePreview = URL.createObjectURL(file) } catch(e){ this.imagePreview='' } 
      } else { 
        this.imagePreview = this.form.image_url || '' 
      } 
    },
    onImageUrlChange(val){
      if(!this.form.image){
         this.imagePreview = val
      }
    },
    onEditImageChange(file){ if (file) { try { this.editImagePreview = URL.createObjectURL(file) } catch(e){ this.editImagePreview='' } } else { this.editImagePreview='' } },
    getFirstItem(content) {
      if (!content) return '';
      let c = String(content);
      
      // Regex for "2、" or "2." at start of line or start of string
      const regex = /(^|\n)\s*2[、.]/;
      const match = c.match(regex);
      if (match) {
          return c.substring(0, match.index).trim();
      }
      
      // Fallback: first line
      const newlineIdx = c.indexOf('\n');
      if (newlineIdx > -1) {
          return c.substring(0, newlineIdx).trim();
      }
      
      return c;
    },
    loadTables(page=1){
      this.loading = true
      this.currentPage = page
      try {
        sessionStorage.setItem('topic_search_page', page)
      } catch (e) {
        void e
      }
      const params = { page: this.currentPage, limit: this.perPage, brief: 1 }
      if (this.searchQuery) params.q = this.searchQuery.trim()
      if (this.sortMode === 'strength') {
        params.sort_by = this.strengthSort || 'strength'
        if (this.selectedRankDate) {
          params.trade_date = this.selectedRankDate
        }
      }
      
      request_zzquant({ url:'/v3/topic/tables', method:'GET', params })
        .then(res=>{
          const d = res && res.data && res.data.data ? res.data.data : {}
          if(Array.isArray(d)){
             this.tables = d
             this.totalRows = d.length
          } else {
             this.tables = Array.isArray(d.items) ? d.items : []
             this.totalRows = d.total || 0
             if (d.trade_date) {
               this.currentTradeDate = d.trade_date
               if (!this.selectedRankDate && this.sortMode === 'strength') {
                 this.selectedRankDate = d.trade_date
               }
             }
          }
        })
        .catch((err)=>{
          this.tables = []
          if (err && err.response && err.response.status === 403) {
            const detail = (err.response.data && err.response.data.detail) || '强度排名与历史回溯为题材表格VIP专属功能'
            this.$bvToast.toast(detail, { title: '👑 权限提示', variant: 'warning', solid: true })
            this.sortMode = 'default'
            this.loadTables(1)
          }
        })
        .finally(()=>{ this.loading = false })
    },
    openCreateSidebar() {
      this.form = { name: '', content: '', source: '', article_id: '', image: null, image_url: '', create_time: '', update_time: '' }
      this.imagePreview = ''
      this.showCreateSidebar = true
    },
    onSearchInput() {
      try {
        sessionStorage.setItem('topic_search_query', this.searchQuery || '')
      } catch (e) {
        void e
      }
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.loadTables(1)
      }, 500)
    },
    async recognizeImage(){
      if(!this.form.image && !this.form.image_url){ this.ocrMsg = '请先选择图片'; return }
      this.submitting = true
      this.showRecognitionArea = true
      this.ocrMsg = '正在处理图片…'
      
      let fileToUpload = this.form.image
      if(fileToUpload){
          try {
              fileToUpload = await this.compressImage(fileToUpload)
          } catch(e) {
              console.error('Compression failed, using original', e)
          }
      }

      this.ocrMsg = '正在识别…'
      const fd = new FormData()
      if(fileToUpload) fd.append('image', fileToUpload)
      else if(this.form.image_url) fd.append('image_url', this.form.image_url)
      fd.append('engine', 'qwen')
      request_zzquant({ url:'/v3/topic/table/ocr', method:'POST', data: fd, headers:{ 'Content-Type':'multipart/form-data' }, timeout: 180000 })
        .then(res=>{
          const d = res.data || {}
          if(d.code === 20000 || d.code === 200){
            const rows = d.data || []
            this.recognized = Array.isArray(rows) ? rows : []
            this.ocrMsg = this.recognized.length ? '识别成功' : '未识别到表格内容'
          } else {
            const msg = d.data || d.message || '识别失败'
            this.recognized = []
            this.ocrMsg = typeof msg === 'string' ? msg : '识别失败'
          }
        })
        .catch(err=>{ const msg = (err && err.message) ? String(err.message) : '识别失败'; this.recognized = []; this.ocrMsg = msg })
        .finally(()=>{ this.submitting = false })
    },
    async recognizeEditImage(mode = 'overwrite'){
      if(!this.editForm.image){ this.editOcrMsg = '请先选择图片'; return }
      this.submitting = true
      this.editOcrMsg = '正在处理图片…'
      
      let fileToUpload = this.editForm.image
      try {
          fileToUpload = await this.compressImage(fileToUpload)
      } catch(e) {
          console.error('Compression failed, using original', e)
      }

      this.editOcrMsg = '正在识别…'
      const fd = new FormData()
      fd.append('image', fileToUpload)
      fd.append('engine', 'qwen')
      request_zzquant({ url:'/v3/topic/table/ocr', method:'POST', data: fd, headers:{ 'Content-Type':'multipart/form-data' }, timeout: 60000 })
        .then(res=>{
          const d = res.data || {}
          if(d.code === 20000 || d.code === 200){
            const rows = d.data || []
            const newRows = Array.isArray(rows) ? rows : []
            if (mode === 'append') {
              this.editForm.rows = [...(this.editForm.rows || []), ...newRows]
              this.editOcrMsg = newRows.length ? '识别成功并已追加内容' : '未识别到表格内容'
            } else {
              this.editForm.rows = newRows
              this.editOcrMsg = newRows.length ? '识别成功并已覆盖内容' : '未识别到表格内容'
            }
          } else {
            const msg = d.data || d.message || '识别失败'
            this.editOcrMsg = typeof msg === 'string' ? msg : '识别失败'
          }
        })
        .catch(err=>{ const msg = (err && err.message) ? String(err.message) : '识别失败'; this.editOcrMsg = msg })
        .finally(()=>{ this.submitting = false })
    },
    createTable(){
      if(!this.canEdit) return
      const name = (this.form.name||'').trim()
      const content = (this.form.content||'').trim()
      if(!name) return
      this.submitting = true
      const fd = new FormData()
      fd.append('name', name)
      fd.append('content', content)
      if(this.form.source) fd.append('source', this.form.source)
      if(this.form.article_id) fd.append('article_id', this.form.article_id)
      if(this.form.create_time) fd.append('created_time', this.form.create_time)
      if(this.form.update_time) fd.append('updated_time', this.form.update_time)
      if(this.form.image) fd.append('image', this.form.image)
      else if(this.form.image_url) fd.append('image_url', this.form.image_url)
      if(this.recognized && this.recognized.length) fd.append('rows', JSON.stringify(this.recognized))
      request_zzquant({ url:'/v3/topic/table/create', method:'POST', data: fd })
        .then(res=>{
          const d = res.data || {}
          if(d.code === 20000 || d.code === 200){
            this.form={ name:'', content:'', image:null, image_url:'', create_time:'', update_time:'' }
            this.imagePreview=''
            this.recognized=[]
            this.loadTables()
            // this.$bvToast.toast('创建成功', { variant:'success', solid:true })
          } else {
            const msg = d.data || d.message || '创建失败'
            alert(typeof msg === 'string' ? msg : '创建失败')
          }
        })
        .catch(err=>{ console.error(err); alert('请求失败') })
        .finally(()=>{ this.submitting=false })
    },
    selectTable(item){ 
      sessionStorage.setItem('current_topic', JSON.stringify(item));
      this.$router.push({ path: `/wonder/topic/detail/${item.unique_key || item.id}` });
    },
    editTable(item){ 
      sessionStorage.setItem('current_topic', JSON.stringify(item));
      this.$router.push({ path: `/wonder/topic/detail/${item.unique_key || item.id}`, query: { edit: 1 } });
    },
    toggleTop(table) {
      if (!this.canEdit) return
      const isTop = !!table.is_top
      const action = isTop ? 'untop' : 'top'
      const tid = table.unique_key || table.id
      
      // Use FastAPI endpoint for new feature
      request_zzquant({ 
        url: `/v3/topic/table/${tid}/${action}`, 
        method: 'POST' 
      })
      .then(res => {
        const d = res.data || {}
        if (d.code === 20000) {
          this.loadTables(this.currentPage)
        } else {
          alert(d.message || '操作失败')
        }
      })
      .catch(err => {
        alert(err.message || '请求失败')
      })
    },
    confirmDeleteTopic(table) {
      if (!this.userRoles.includes('admin')) return
      this.$bvModal.msgBoxConfirm(`确定要逻辑删除题材「${this.formatTopicName(table.name)}」吗？删除后普通用户不可见，回测与实盘将排除此题材。`, {
        title: '确认删除题材',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: '确认删除',
        cancelTitle: '取消',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      }).then(value => {
        if (value) {
          this.doDeleteTopic(table)
        }
      })
    },
    doDeleteTopic(table) {
      const tid = table.unique_key || table.id
      request_zzquant({
        url: `/v3/topic/table/${tid}/delete`,
        method: 'POST'
      }).then(res => {
        const d = res.data || {}
        if (d.code === 20000) {
          this.$bvToast.toast('题材已逻辑删除', { variant: 'success', solid: true })
          this.loadTables(this.currentPage)
        } else {
          alert(d.message || '删除失败')
        }
      }).catch(err => {
        console.error(err)
        alert('请求失败')
      })
    },
    confirmRestoreTopic(table) {
      if (!this.userRoles.includes('admin')) return
      this.$bvModal.msgBoxConfirm(`确定要恢复题材「${this.formatTopicName(table.name)}」吗？恢复后将重新展示给普通用户。`, {
        title: '确认恢复题材',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'success',
        okTitle: '确认恢复',
        cancelTitle: '取消',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      }).then(value => {
        if (value) {
          this.doRestoreTopic(table)
        }
      })
    },
    doRestoreTopic(table) {
      const tid = table.unique_key || table.id
      request_zzquant({
        url: `/v3/topic/table/${tid}/restore`,
        method: 'POST'
      }).then(res => {
        const d = res.data || {}
        if (d.code === 20000) {
          this.$bvToast.toast('题材已恢复展示', { variant: 'success', solid: true })
          this.loadTables(this.currentPage)
        } else {
          alert(d.message || '恢复失败')
        }
      }).catch(err => {
        console.error(err)
        alert('请求失败')
      })
    },
    onPinnedSortEnd() {
      // 触发排序后，调用接口保存最新的置顶顺序
      const ids = this.pinnedTables.map(t => t.id)
      if (ids.length <= 1) return
      
      request_zzquant({
        url: '/v3/topic/tables/top/sort',
        method: 'POST',
        data: { ids }
      })
      .then(res => {
        const d = res.data || {}
        if (d.code === 20000) {
          // 可选：重新加载列表以确保顺序同步
          // this.loadTables(this.currentPage)
        } else {
          this.$bvToast.toast(d.message || '排序保存失败', { variant: 'danger' })
          this.loadTables(this.currentPage) // 回滚
        }
      })
      .catch(err => {
        this.$bvToast.toast(err.message || '请求失败', { variant: 'danger' })
        this.loadTables(this.currentPage) // 回滚
      })
    },
    generateMindMap(){
      const data = { name: this.editForm.name || '题材', children: [] };
      const map = {};
      // Layout config
      const layoutMap = {
          'LR': { orient: 'LR', top: '5%', left: '5%', bottom: '5%', right: '70%', labelPos: 'left', leafLabelPos: 'right', labelAlign: 'right', leafLabelAlign: 'left' },
          'RL': { orient: 'RL', top: '5%', left: '70%', bottom: '5%', right: '5%', labelPos: 'right', leafLabelPos: 'left', labelAlign: 'left', leafLabelAlign: 'right' },
          'TB': { orient: 'TB', top: '5%', left: '5%', bottom: '70%', right: '5%', labelPos: 'top', leafLabelPos: 'bottom', labelAlign: 'center', leafLabelAlign: 'center', labelVertical: 'bottom', leafLabelVertical: 'top' },
          'BT': { orient: 'BT', top: '70%', left: '5%', bottom: '5%', right: '5%', labelPos: 'bottom', leafLabelPos: 'top', labelAlign: 'center', leafLabelAlign: 'center', labelVertical: 'top', leafLabelVertical: 'bottom' }
      };
      const currentStyle = layoutMap[this.mindMapStyle] || layoutMap['LR'];
      const stockLabel = {
          position: currentStyle.leafLabelPos,
          verticalAlign: currentStyle.leafLabelVertical || 'middle',
          align: currentStyle.leafLabelAlign,
          fontSize: 12,
          rotate: (this.mindMapStyle === 'TB' || this.mindMapStyle === 'BT') ? 90 : 0
      };

      (this.editForm.rows || []).forEach(row => {
          const l1 = (row['一级大类'] || '其他').trim();
          const l2 = (row['二级小类'] || '').trim();
          const l3 = (row['三级细分'] || '').trim();
          const stock = (row['个股'] || '').trim();
          if (!stock) return;

          let stockName = stock;
          let nodeColor = undefined;
          let nodeFontWeight = undefined;

          if (this.showQuotes && row['涨跌幅'] !== undefined && row['涨跌幅'] !== null) {
             stockName += ` (${row['涨跌幅']}%)`;
             const rate = parseFloat(row['涨跌幅']);
             if (!isNaN(rate)) {
                if (rate > 9) {
                    nodeColor = 'red';
                    nodeFontWeight = 'bold';
                } else if (rate > 0) {
                    nodeColor = 'red';
                } else if (rate < 0) {
                    nodeColor = 'green';
                }
             }
          }

          if (!map[l1]) map[l1] = {};
          if (!map[l1][l2]) map[l1][l2] = {};
          if (!map[l1][l2][l3]) map[l1][l2][l3] = [];

          map[l1][l2][l3].push({ 
             name: stockName, 
             value: row['相关性'] || '', 
             source: row['信息源'] || '',
             color: nodeColor,
             fontWeight: nodeFontWeight
          });
      });

      // Colors for different branches
      const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'];
      let colorIndex = 0;

      for (const l1 in map) {
          const color = colors[colorIndex % colors.length];
          colorIndex++;
          const l1Node = { 
            name: l1, 
            children: [],
            itemStyle: { color: color, borderColor: color },
            lineStyle: { color: color }
          };
          for (const l2 in map[l1]) {
                  let l2Parent = l1Node;
                  if (l2) {
                    const l2Node = { 
                      name: l2, 
                      children: [],
                      itemStyle: { color: color, borderColor: color },
                      lineStyle: { color: color }
                    };
                    l1Node.children.push(l2Node);
                    l2Parent = l2Node;
                  }

                  for (const l3 in map[l1][l2]) {
                      let l3Parent = l2Parent;
                      if (l3) {
                          const l3Node = { 
                              name: l3, 
                              children: [],
                              itemStyle: { color: color, borderColor: color },
                              lineStyle: { color: color }
                          };
                          l2Parent.children.push(l3Node);
                          l3Parent = l3Node;
                      }

                      map[l1][l2][l3].forEach(s => {
                          const stockNode = {
                              name: s.name,
                              children: [],
                              itemStyle: { color: color, borderColor: color },
                              lineStyle: { color: color },
                              label: {
                                  ...stockLabel,
                                  color: s.color,
                                  fontWeight: s.fontWeight
                              }
                          };
                          // Helper to truncate text
                          const truncate = (str, len=200) => str.length > len ? str.substring(0, len) + '...' : str;
                          
                          let lastNode = stockNode;
        
                          if (this.canEdit && s.source) {
                            const sourceNode = { 
                              name: truncate(s.source) + '：' + truncate(s.value),
                              value: s.value, 
                              itemStyle: { color: color, borderColor: color },
                              lineStyle: { color: color },
                              children: []
                            }
                            lastNode.children.push(sourceNode)
                          } else if (s.value) {
                            const relNode = { 
                              name: truncate(s.value),
                              value: s.value, 
                              itemStyle: { color: color, borderColor: color },
                              lineStyle: { color: color },
                              children: []
                            }
                            lastNode.children.push(relNode)
                          }
                          
                          l3Parent.children.push(stockNode);
                      });
                  }
              }
          data.children.push(l1Node);
      }

      const getLayoutOption = (style) => {
          const base = {
              type: 'tree',
              data: [data],
              symbol: this.mindMapSymbol,
              edgeShape: this.mindMapCurve,
              symbolSize: 7,
              roam: true,
              expandAndCollapse: true,
              initialTreeDepth: -1,
              animationDuration: 550,
              animationDurationUpdate: 750
          };
          
          if (style === 'radial') {
              return {
                  ...base,
                  layout: 'radial',
                  top: '10%', left: '10%', bottom: '10%', right: '10%',
                  label: { position: 'right', verticalAlign: 'middle', align: 'left', fontSize: 12 },
                  leaves: { label: { position: 'right', verticalAlign: 'middle', align: 'left' } }
              };
          }
          
          const opt = layoutMap[style] || layoutMap['LR'];
          return {
              ...base,
              layout: 'orthogonal',
              orient: opt.orient,
              top: opt.top, left: opt.left, bottom: opt.bottom, right: opt.right,
              label: { 
                  position: opt.labelPos, 
                  verticalAlign: opt.labelVertical || 'middle', 
                  align: opt.labelAlign, 
                  fontSize: 12,
                  rotate: (style === 'TB' || style === 'BT') ? 90 : 0
              },
              leaves: { 
                  label: { 
                      position: opt.leafLabelPos, 
                      verticalAlign: opt.leafLabelVertical || 'middle', 
                      align: opt.leafLabelAlign,
                      rotate: (style === 'TB' || style === 'BT') ? 90 : 0
                  } 
              }
          };
      };

      this.mindMapOption = {
          title: [
            {
              text: this.editForm.updated_time ? '题材更新时间：' + this.editForm.updated_time : '',
              top: 10,
              left: 10,
              textStyle: { fontSize: 12, color: '#999', fontWeight: 'normal' }
            },
            {
              text: this.lastQuoteTime ? '行情更新时间：' + this.lastQuoteTime : '',
              top: 10,
              right: 10,
              textStyle: { fontSize: 12, color: '#999', fontWeight: 'normal' }
            }
          ],
          backgroundColor: this.theme === 'dark' ? '#1c1c1e' : '#ffffff',
          tooltip: { 
            trigger: 'item', 
            triggerOn: 'mousemove',
            formatter: (params) => {
              // Show full text if available in value, otherwise name
              return params.data.value ? params.data.value : params.name;
            }
          },
          series: [getLayoutOption(this.mindMapStyle)],
          graphic: [
            {
              type: 'text',
              z: -10,
              right: 20,
              bottom: 60,
              style: {
                fill: '#e0e0e0',
                text: '自在量化',
                font: 'bold 50px sans-serif'
              }
            },
            {
              type: 'text',
              z: -10,
              right: 20,
              bottom: 20,
              style: {
                fill: '#e0e0e0',
                text: 'quant.zizizaizai.com',
                font: '24px sans-serif'
              }
            },
            {
              type: 'text',
              z: -10,
              left: 'center',
              bottom: 10,
              style: {
                fill: '#999999',
                text: '声明：本文涉及的资讯、数据等内容来自网络公共信息，我们对以上信息的准确性和完整性不做任何保证，涉及的信息随时都可能会有新变化，仅作为学习交流之用。',
                font: '12px sans-serif'
              }
            }
          ]
      };
    },
    changeMindMapStyle(style){
      this.mindMapStyle = style;
      this.generateMindMap();
    },
    changeMindMapCurve(curve){
      this.mindMapCurve = curve;
      this.generateMindMap();
    },
    changeMindMapSymbol(symbol){
      this.mindMapSymbol = symbol;
      this.generateMindMap();
    },
    queryStockReturns(){
      if(!this.editForm.rows || !this.editForm.rows.length) return
      
      const stocks = this.editForm.rows.map(r => r['个股']).filter(s => s && s.trim())
      if(!stocks.length){
        alert('没有找到个股代码')
        return
      }
      
      this.loadingStocks = true
      this.lastQuoteTime = this.formatNow()
      
      request_zzquant({ 
        url:'/v3/topic/table/stock-returns', 
        method:'POST', 
        data: { stocks } 
      })
      .then(res => {
         const d = res.data
         if(d.code === 20000){
            const result = d.data || {}
            // Update rows with new data
            let updatedCount = 0
            const pickCircCap = (obj) => {
              if (!obj || typeof obj !== 'object') return ''
              const keys = ['circ_cap','circulation_cap','circulating_cap','circ','circ_price','circ_value','circulation_value']
              for (let i=0;i<keys.length;i++){
                const k = keys[i]
                if (obj[k] !== undefined && obj[k] !== null) return obj[k]
              }
              return ''
            }
            this.editForm.rows = this.editForm.rows.map(row => {
               const stock = (row['个股'] || '').trim()
               if(stock && result[stock]){
                  updatedCount++
                  return {
                     ...row,
                     '涨跌幅': result[stock].px_change_rate,
                     '最新价': result[stock].last_px,
                     '流通市值': pickCircCap(result[stock]),
                     'symbol_code': result[stock].symbol || result[stock].code || result[stock].ts_code || result[stock].symbol_code
                  }
               }
               return row
            })
            
            // Refresh mind map to show price changes
            if(this.viewMode === 'mindmap'){
               this.generateMindMap()
            }

            this.$bvToast.toast(`已更新 ${updatedCount} 个股票行情`, { variant:'success', solid:true })
         } else {
            alert(d.message || '查询失败')
         }
      })
      .catch(e => {
         console.error(e)
         alert('查询出错')
      })
      .finally(() => {
         this.loadingStocks = false
      })
    },
    onToggleQuotes(val){
      if(val){
        this.queryStockReturns()
      } else {
        // Just regenerate the mindmap to remove rate color/label
        if(this.viewMode === 'mindmap'){
          this.generateMindMap()
        }
      }
    },
    formatNow(){
      const d = new Date()
      const pad = n => String(n).padStart(2, '0')
      const y = d.getFullYear()
      const m = pad(d.getMonth()+1)
      const day = pad(d.getDate())
      const hh = pad(d.getHours())
      const mm = pad(d.getMinutes())
      const ss = pad(d.getSeconds())
      return `${y}-${m}-${day} ${hh}:${mm}:${ss}`
    },
    switchToJsonMode(){
      this.jsonStr = JSON.stringify(this.editForm.rows, null, 2);
      this.viewMode = 'json';
    },
    enterMindMapFullscreen(){
      const el = this.$refs.mindmapContainer
      if (!el) return
      try {
        if (el.requestFullscreen) el.requestFullscreen()
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen()
        else if (el.msRequestFullscreen) el.msRequestFullscreen()
      } catch(e) { console.warn('enterMindMapFullscreen failed', e) }
    },
    downloadMindMap(){
      const chartRef = this.$refs.mindmapChart
      if (!chartRef) { alert('未找到图表'); return }
      let inst = null
      try {
        if (typeof chartRef.getEchartsInstance === 'function') {
          inst = chartRef.getEchartsInstance()
        } else if (chartRef.chart) {
          inst = chartRef.chart
        } else if (chartRef.echarts) {
          inst = chartRef.echarts
        }
      } catch(e) { console.warn('get chart instance failed', e) }
      if (!inst || typeof inst.getDataURL !== 'function') { alert('无法获取图像'); return }
      const dataURL = inst.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' })
      const a = document.createElement('a')
      const name = (this.editForm.name || '思维导图')
      const ts = this.lastQuoteTime || this.editForm.updated_time || ''
      a.href = dataURL
      a.download = `${name}-${ts || 'snapshot'}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    switchToTableMode(){
      if(this.viewMode === 'json'){
        this.syncJsonToRows();
      }
      this.viewMode = 'table';
    },
    switchToMindMap(){
      this.viewMode = 'mindmap';
      this.generateMindMap();
    },
    syncJsonToRows(){
       try {
          const parsed = JSON.parse(this.jsonStr);
          if (Array.isArray(parsed)) {
            this.editForm.rows = parsed;
            this.jsonError = '';
          } else {
            this.jsonError = 'JSON必须是数组格式';
          }
        } catch (e) {
          this.jsonError = 'JSON格式错误: ' + e.message;
        }
    },
    updateTable(){
      if (this.viewMode === 'json') {
          this.syncJsonToRows();
          if (this.jsonError) {
             alert(this.jsonError);
             return;
          }
      }
      if(!this.canEdit || !this.editId) return
      this.submitting = true
      const payload = { 
        name: this.editForm.name, 
        content: this.editForm.content, 
        rows: this.editForm.rows,
        created_time: this.editForm.created_time,
        updated_time: this.editForm.updated_time
      }
      request_zzquant({ url:'/v3/topic/table/'+this.editId, method:'POST', data: payload })
        .then(res=>{
          const d = res.data || {}
          if(d.code === 20000 || d.code === 200){
            this.showEdit=false
            this.loadTables()
          } else {
            const msg = d.data || d.message || '更新失败'
            alert(typeof msg === 'string' ? msg : '更新失败')
          }
        })
        .catch(err=>{ console.error(err); alert('请求失败') })
        .finally(()=>{ this.submitting=false })
    },
    addRow(index) {
      const newRow = {
        '一级大类': '',
        '二级小类': '',
        '三级细分': '',
        '个股': '',
        '相关性': '',
        '信息源': ''
      };
      this.editForm.rows.splice(index + 1, 0, newRow);
    },
    getRateStyle(rate) {
        if (rate === undefined || rate === null) return {};
        const r = parseFloat(rate);
        if (isNaN(r)) return {};
        if (r > 9) return { color: 'red', fontWeight: 'bold' };
        if (r > 5) return { color: 'red', fontWeight: 'bold' };
        if (r > 0) return { color: 'red' };
        if (r < 0) return { color: 'green' };
        return {};
    },
    switchToRecognitionJsonMode(){
      this.recognitionJsonStr = JSON.stringify(this.recognized, null, 2);
      this.recognitionView = 'json';
    },
    syncRecognitionJsonToTable(){
       try {
          const parsed = JSON.parse(this.recognitionJsonStr);
          if (Array.isArray(parsed)) {
            this.recognized = parsed;
            this.recognitionJsonError = '';
          } else {
            this.recognitionJsonError = 'JSON必须是数组格式';
          }
        } catch (e) {
          this.recognitionJsonError = 'JSON格式错误: ' + e.message;
        }
    },
    addRecognizedRow(){
      this.recognized.push({
        '一级大类': '',
        '二级小类': '',
        '三级细分': '',
        '个股': '',
        '相关性': '',
        '信息源': '',
        '涨跌幅': '',
        '最新价': ''
      })
    },
    openSidebar(item) {
        if (!item || !item.symbol_code) {
             // Try to use stock name if code is missing, though sidebar needs code usually.
             // But let's just warn or do nothing if no code.
             // Or maybe we can try to guess? No, let's rely on queryStockReturns populating it.
             if (this.showQuotes && item['个股']) {
                 // Maybe alert user to refresh quotes?
                 console.warn('No symbol code for stock:', item['个股'])
             }
             return
        }
        this.sidebarSymbolCode = item.symbol_code
        this.sidebarSymbolName = item['个股']
        this.sidebarDateMark = '' // Current date by default
        this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
    },
    onRowClicked(item){ this.selectTable(item) },
    showTopicKline(table) {
      const uniqueKey = table.unique_key || table.id
      if (uniqueKey) {
        this.selectedTopicUniqueKey = String(uniqueKey)
        this.selectedTopicName = table.name
        this.showTopicKlineModal = true
      }
    },
    getRankClass(idx) {
      const absIdx = (this.currentPage - 1) * this.perPage + idx
      if (absIdx === 0) return 'rank-1'
      if (absIdx === 1) return 'rank-2'
      if (absIdx === 2) return 'rank-3'
      return 'rank-other'
    },
    setSortMode(mode) {
      if (mode === 'strength') {
        if (!this.userHasPermission) {
          this.$bvToast.toast('强度排名与历史回溯为题材表格VIP专属功能，请先开通订阅', {
            title: '👑 VIP专属特权',
            variant: 'warning',
            solid: true,
          })
          this.$router.push({ path: '/subscription', query: { module: 'topic_kline' } })
          return
        }
        this.sortMode = 'strength'
      } else {
        this.sortMode = 'default'
      }
      this.loadTables(1)
    },
    changeStrengthSort(type) {
      this.strengthSort = type
      this.loadTables(1)
    },
    shiftTradeDate(days) {
      const baseDate = this.selectedRankDate || this.currentTradeDate || this.todayStr
      const parts = baseDate.split('-').map(Number)
      const d = new Date(parts[0], parts[1] - 1, parts[2])
      d.setDate(d.getDate() + days)

      // 跳过周末
      if (days > 0 && d.getDay() === 6) {
        d.setDate(d.getDate() + 2)
      } else if (days < 0 && d.getDay() === 0) {
        d.setDate(d.getDate() - 2)
      }

      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const dateStr = `${y}-${m}-${String(d.getDate()).padStart(2, '0')}`
      if (dateStr > this.todayStr) return
      this.selectedRankDate = dateStr
      this.loadTables(1)
    },
    resetToLatestDate() {
      this.selectedRankDate = ''
      this.loadTables(1)
    },
    onDateChanged(newDate) {
      this.selectedRankDate = newDate || ''
      this.loadTables(1)
    },
    getStageTagVariant(tag) {
      if (!tag) return 'secondary'
      if (tag.includes('启动')) return 'success'
      if (tag.includes('主升') || tag.includes('加速')) return 'danger'
      if (tag.includes('高潮') || tag.includes('分化') || tag.includes('分歧')) return 'warning'
      if (tag.includes('退潮')) return 'dark'
      if (tag.includes('蓄势') || tag.includes('休整')) return 'info'
      if (tag.includes('轮动')) return 'secondary'
      return 'info'
    }
  }
}
</script>

<style>
/* 去掉之前的modal样式 */
.cursor-pointer {
  cursor: pointer;
}
@media (max-width: 768px) {
  .wonder-table-view-small td.col-content { min-width: 150px; }
  
  /* 移动端字体与间距 */
  .wonder-table-view-small th, 
  .wonder-table-view-small td {
    padding: 2px !important;
    font-size: 10px !important;
  }
}

/* Mobile optimizations */
@media (max-width: 768px) {
  /* ... existing styles ... */
  
  /* Hide content column on mobile */
  ::v-deep .table .d-none.d-md-table-cell {
    display: none !important;
  }
}
.mindmap-container {
  background-color: var(--bg-card, #ffffff);
}

/* 订阅提示层样式 - 缩小尺寸 */
.sub-prompt-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 110;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
}

.premium-card {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border: 1px solid rgba(255, 193, 7, 0.3);
  box-shadow: 0 20px 40px rgba(0,0,0,0.4);
  border-radius: 20px;
  position: relative;
  overflow: hidden;
}

.premium-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,193,7,0.05) 0%, transparent 70%);
  pointer-events: none;
}

.premium-badge-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ffc107 0%, #ff8f00 100%);
  color: #fff;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto;
  box-shadow: 0 8px 16px rgba(255, 143, 0, 0.3);
  animation: premium-pulse 2s infinite;
}

.premium-btn {
  background: linear-gradient(90deg, #ffc107, #ff9800, #ffc107) !important;
  background-size: 200% auto !important;
  border: none !important;
  color: #000 !important;
  transition: 0.5s !important;
}

.premium-btn:hover {
  background-position: right center !important;
  transform: scale(1.02);
}
.premium-btn:disabled,
.premium-btn.disabled {
  background: #6c757d !important;
  color: #fff !important;
  cursor: not-allowed;
  box-shadow: none !important;
  transform: none !important;
}

@keyframes premium-pulse {
  0% { box-shadow: 0 0 0 0 rgba(255, 143, 0, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(255, 143, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 143, 0, 0); }
}

/* 锁定遮罩样式 */
.locked-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: rgba(var(--bg-card-rgb, 255, 255, 255), 0.2);
  backdrop-filter: blur(12px) saturate(180%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

body.theme-dark .locked-overlay {
  background: rgba(15, 23, 42, 0.3);
}

.lock-circle {
  width: 56px;
  height: 56px;
  background: rgba(255, 193, 7, 0.15);
  color: #ffc107;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  border: 1px solid rgba(255, 193, 7, 0.3);
  box-shadow: 0 0 20px rgba(255, 193, 7, 0.1);
  animation: lock-float 3s ease-in-out infinite;
}

.blur-content {
  filter: blur(12px);
  pointer-events: none;
  opacity: 0.4;
  transition: all 0.4s ease;
}

/* 泡泡消息优化 */
.alert-bubble {
  background: rgba(var(--bg-muted-rgb, 248, 250, 252), 0.7) !important;
  backdrop-filter: blur(4px);
  border: 1px solid var(--border-color, #e2e8f0) !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.alert-bubble:hover {
  transform: translateX(4px);
  background: rgba(var(--bg-muted-rgb, 248, 250, 252), 0.9) !important;
}

.premium-fade-enter-active, .premium-fade-leave-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.premium-fade-enter, .premium-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -30%) scale(0.9);
}

@keyframes lock-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

/* 监控面板过渡动画 */
.monitor-slide-enter-active, .monitor-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.monitor-slide-enter, .monitor-slide-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* 悬浮触碰器 */
.monitor-float-trigger {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 80px;
  background: var(--primary, #007bff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px 0 0 20px;
  cursor: pointer;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 20px;
  box-shadow: -4px 0 15px rgba(0,0,0,0.1);
}

.monitor-float-trigger:hover {
  width: 54px;
  padding-right: 10px;
  background: var(--primary-dark, #0056b3);
}

body.theme-dark .monitor-float-trigger {
  background: #1e293b;
  border: 1px solid #334155;
  border-right: none;
  color: #ffc107;
  box-shadow: -4px 0 15px rgba(0,0,0,0.3);
}

.monitor-float-trigger .pulse-warning {
  animation: float-pulse 2s infinite;
}

@keyframes float-pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
}

/* 简单的渐变动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>

<style>

/* --- Topic UI Overhaul --- */
.topic-card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #f0f0f0;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.topic-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.08);
  border-color: #007bff44;
}

.topic-rank {
  width: 32px;
  height: 44px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.1rem;
  flex-shrink: 0;
  color: #fff;
}
.rank-1 { background-color: #e33e33; }
.rank-2 { background-color: #f7b500; }
.rank-3 { background-color: #f7b500; }
.rank-other { background-color: #f8f9fa; border: 1px solid #e9ecef; color: #6c757d; }

body.theme-dark .rank-other {
  background-color: #0f172a;
  border-color: #334155;
  color: #60a5fa;
}

.topic-title {
  font-size: 0.95rem;
  letter-spacing: -0.01em;
}

.topic-desc {
  line-height: 1.5;
  color: #6c757d;
}

.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.btn-refresh {
  border: 1px solid #e0e0e0;
  color: #555;
  font-weight: 500;
}

.variant-soft-info {
  background-color: #e3f2fd;
  color: #0d47a1;
}

.custom-pagination ::v-deep .page-link {
  border-radius: 8px !important;
  margin: 0 2px;
  border: none;
  background-color: #f8f9fa;
  color: #6c757d;
}

.custom-pagination ::v-deep .page-item.active .page-link {
  background-color: #007bff;
  color: #fff;
  box-shadow: 0 4px 8px rgba(0,123,255,0.3);
}

.transition-all {
  transition: all 0.2s ease-in-out;
}

.hover-primary:hover {
  color: #007bff !important;
  transform: scale(1.1);
}

/* --- Dark Mode Extensions --- */
body.theme-dark .bg-light {
  background-color: #0f172a !important;
}

body.theme-dark .topic-card {
  background-color: #1e293b;
  border-color: #334155;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

body.theme-dark .topic-card:hover {
  border-color: #007bff;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
}

body.theme-dark .topic-title {
  color: #60a5fa !important;
}

body.theme-dark .topic-desc {
  color: #94a3b8;
}

body.theme-dark .topic-time {
  color: #64748b !important;
}

body.theme-dark .btn-refresh {
  background-color: #1e293b;
  border-color: #334155;
  color: #cbd5e1;
}

body.theme-dark .btn-refresh:hover {
  background-color: #334155;
}

.search-group {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--border-color, #e2e8f0);
  background-color: var(--bg-card, #ffffff);
  height: 48px;
  transition: all 0.2s ease;
}

.search-group:focus-within {
  border-color: var(--accent-color, #3b82f6);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
}

.search-group ::v-deep .input-group-text {
  background-color: transparent !important;
  border: none !important;
  padding-left: 16px;
  padding-right: 8px;
  display: flex;
  align-items: center;
}

.search-group ::v-deep input {
  height: 100% !important;
  border: none !important;
  padding-left: 8px !important;
  padding-right: 16px !important;
  font-size: 15px;
  background-color: transparent !important;
  color: var(--text-main, #1e293b);
  box-shadow: none !important;
}

body.theme-dark .search-group {
  background-color: #1e293b !important;
  border-color: #334155 !important;
}

body.theme-dark .search-group ::v-deep input {
  color: #f1f5f9 !important;
}

body.theme-dark .custom-pagination ::v-deep .page-link {
  background-color: #1e293b;
  color: #94a3b8;
}

body.theme-dark .custom-pagination ::v-deep .page-item.disabled .page-link {
  background-color: #0f172a;
  color: #475569;
}

/* --- Sidebar Refinement --- */
.sidebar-form-container {
  font-size: 14px;
}
.sidebar-card {
  border: none;
  background: transparent;
}

body.theme-dark .sidebar-card {
  background: transparent;
}

body.theme-dark ::v-deep .b-sidebar {
  background-color: #0f172a !important;
}

body.theme-dark ::v-deep .b-sidebar-header {
  background-color: #1e293b !important;
  color: #f1f5f9 !important;
  border-bottom: 1px solid #334155 !important;
}

body.theme-dark ::v-deep .b-sidebar-body {
  background-color: #0f172a !important;
}

.recognition-table-wrapper ::v-deep .table {
  font-size: 13px;
}

body.theme-dark .recognition-table-wrapper {
  border-color: #334155;
  background-color: #1e293b !important;
}

body.theme-dark .recognition-table-wrapper ::v-deep .form-control {
  background-color: transparent !important;
  color: #f1f5f9 !important;
}

body.theme-dark .recognition-table-wrapper ::v-deep table {
  color: #cbd5e1 !important;
}

body.theme-dark .recognition-table-wrapper ::v-deep thead th {
  background-color: #334155 !important;
  color: #f1f5f9 !important;
  border-bottom: 1px solid #475569 !important;
}

body.theme-dark .recognition-table-wrapper ::v-deep .table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(255,255,255,0.02) !important;
}

body.theme-dark .custom-input,
body.theme-dark .custom-input-sm {
  background-color: #0f172a !important;
  border-color: #334155 !important;
  color: #f1f5f9 !important;
}

body.theme-dark .bg-card {
  background-color: #1e293b !important;
  border: 1px solid #334155 !important;
}

body.theme-dark .topic-rank.rank-other {
  background: #0f172a;
}

body.theme-dark .image-upload-wrapper {
  background-color: #1e293b !important;
  border-color: #334155 !important;
}

.topic-card-deleted {
  opacity: 0.75;
  border-left: 3px solid #dc3545 !important;
  background-color: rgba(220, 53, 69, 0.04) !important;
}
.hover-danger:hover {
  color: #dc3545 !important;
}
.hover-success:hover {
  color: #28a745 !important;
}

/* 强度排名说明 Popover 样式 */
.topic-rank-help-popover {
  max-width: 500px !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
}
.topic-rank-help-popover .popover-header {
  font-size: 13px;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
body.theme-dark .topic-rank-help-popover {
  background-color: #1e293b !important;
  border-color: #334155 !important;
}
body.theme-dark .topic-rank-help-popover .popover-header {
  background-color: #1e293b !important;
  border-bottom-color: #334155 !important;
  color: #f1f5f9 !important;
}
body.theme-dark .topic-rank-help-popover .popover-body {
  background-color: #1e293b !important;
  color: #cbd5e1 !important;
}
body.theme-dark .topic-rank-help-popover table th {
  background-color: #334155 !important;
  color: #f1f5f9 !important;
  border-color: #475569 !important;
}
body.theme-dark .topic-rank-help-popover table td {
  border-color: #334155 !important;
  color: #cbd5e1 !important;
}
body.theme-dark .topic-rank-help-popover strong.text-dark {
  color: #f8fafc !important;
}
</style>
